import CreateButton from "../components/CreateButton"
import {
    BlockTypeSelect,
    BoldItalicUnderlineToggles,
    diffSourcePlugin,
    DiffSourceToggleWrapper,
    headingsPlugin,
    linkDialogPlugin,
    listsPlugin,
    ListsToggle,
    MDXEditor,
    quotePlugin,
    Separator,
    tablePlugin,
    thematicBreakPlugin,
    toolbarPlugin,
    UndoRedo
} from '@mdxeditor/editor';
import '@mdxeditor/editor/style.css';
import { addDays } from "date-fns";
import QrScanner from 'qr-scanner';
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ChromePicker } from "react-color";
import EasePicker, { AmpPlugin, LockPlugin, PresetPlugin, RangePlugin } from "react-easepick";
import { useTranslation } from "react-i18next";
import ReactMarkdown from 'react-markdown';
import { useNavigate, useParams } from "react-router-dom";
import { Bar, BarChart, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, XAxis, YAxis } from 'recharts';
import rehypeRaw from 'rehype-raw';
import api from "../api";
import EventSponsors from "../components/EventSponsors";
import EventTalentCvs from "../components/EventTalentCvs";
import { ReactComponent as CheckIcon } from "../assets/images/icons/ic-check.svg";
import { ReactComponent as CloseIcon } from "../assets/images/icons/ic-close.svg";
import { ReactComponent as EditIcon } from "../assets/images/icons/ic-edit.svg";
import { ReactComponent as DeleteIcon } from "../assets/images/icons/ic-delete.svg";
import { ReactComponent as DownloadIcon } from "../assets/images/icons/ic-download.svg";
import { ReactComponent as QrCodeIcon } from "../assets/images/icons/ic-qrcode.svg";
import { ReactComponent as ReorderIcon } from "../assets/images/icons/ic-reorder.svg";
import qrCodePlaceholder from "../assets/images/qrcode.png";
import { CompanyType, FileType } from "../common/constants";
import useWindowDimensions from '../common/hooks/useWindowDimensions';
import MetricStatBlock from "../components/analytics/MetricStatBlock";
import StatusBadge from "../components/StatusBadge";
import Breadcrumb from "../components/Breadcrumb";
import Button from '../components/Button';
import Card from "../components/cards/Card";
import AlertDialog from "../components/dialogs/AlertDialog";
import Dialog from "../components/dialogs/Dialog";
import HeaderFooterLayout from '../components/layouts/HeaderFooterLayout';
import Loader from "../components/Loader";
import MultipleSelect from '../components/MultipleSelect';
import Pagination from '../components/Pagination';
import Select from "../components/Select";
import Skeleton from "../components/Skeleton";
import Table from "../components/Table";
import TextInput from "../components/TextInput";
import Toggle from "../components/Toggle";
import TruncatedText from "../components/TruncatedText";
import Uploader from "../components/Uploader";
import { EventPanelType } from '../enums';
import typo from '../typography.module.css';
import { formatDateV2, formatNumber, formatPrice, formatTimeV2 } from "../utils";
import styles from './Event.module.css';
import { parseEventDate } from "../utils/eventDates";

const getMinEventAnalyticsDate = () => new Date(2025, 6, 11, 9, 18);
const SCAN_SUCCESS_RESUME_DELAY_MS = 350;
const SCAN_ERROR_RESUME_DELAY_MS = 900;
const eventNotificationCategoryOptions = [
    { id: 'informational', name: 'Informativa', value: 'informational' },
    { id: 'marketing', name: 'Marketing', value: 'marketing' },
];

const calculatePercentageDifference = (actualTotal, previousTotal) => {
    const actual = Number(actualTotal ?? 0);
    const previous = Number(previousTotal ?? 0);

    if (previous === 0) {
        return actual > 0 ? Infinity : actual < 0 ? -Infinity : 0;
    }

    return ((actual - previous) / Math.abs(previous)) * 100;
};

const formatDateAsRomeDay = (value) => {
    if (!value) {
        return null;
    }

    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) {
        return null;
    }

    const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone: 'Europe/Rome',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).formatToParts(date);

    const year = parts.find((part) => part.type === 'year')?.value;
    const month = parts.find((part) => part.type === 'month')?.value;
    const day = parts.find((part) => part.type === 'day')?.value;

    if (!year || !month || !day) {
        return null;
    }

    return `${year}-${month}-${day}`;
};

const formatPercentage = (value) => {
    const number = Number(value || 0);
    return `${number.toFixed(1)}%`;
};

const formatHourRangeLabel = (hourLabel) => {
    const hour = Number(hourLabel);
    if (Number.isNaN(hour)) {
        return String(hourLabel || '');
    }

    return `${hour}:00 - ${hour + 1}:00`;
};

const getUserApplicationStatusBadge = (statusValue) => {
    switch (statusValue) {
        case 'pending':
            return { name: 'Da contattare', color: 'rgba(84, 135, 255, 100%)' };
        case 'message_sent':
            return { name: 'Messaggio inviato', color: 'rgba(255, 135, 48, 100%)' };
        case 'processing':
            return { name: 'Contattato', color: 'rgba(255, 135, 48, 100%)' };
        case 'call_scheduled':
            return { name: 'Chiamata schedulata', color: 'rgba(251, 217, 101, 100%)' };
        case 'call_completed':
            return { name: 'Chiamata effettuata', color: 'rgba(106, 230, 201, 100%)' };
        case 'approved':
            return { name: 'Candidatura accettata', color: 'rgba(var(--sf-green-rgb), 100%)' };
        case 'rejected':
            return { name: 'Candidatura rifiutata', color: 'rgba(243, 55, 107, 100%)' };
        case 'paid':
            return { name: 'Vinto', color: 'rgba(var(--sf-green-rgb), 100%)' };
        case 'ghost':
            return { name: 'Perso', color: 'rgba(95, 102, 126, 100%)' };
        case 'next_edition':
            return { name: 'Prossima edizione', color: 'rgba(95, 102, 126, 100%)' };
        default:
            return { name: statusValue || '---', color: 'rgba(var(--secondary-rgb), 100%)' };
    }
};

const normalizeSlotDateValue = (value) => {
    if (!value) {
        return '';
    }

    if (value instanceof Date) {
        if (Number.isNaN(value.getTime())) {
            return '';
        }

        const pad = (num) => String(num).padStart(2, '0');
        return `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(value.getDate())}T${pad(value.getHours())}:${pad(value.getMinutes())}:${pad(value.getSeconds())}`;
    }

    if (typeof value === 'string') {
        return value.trim();
    }

    return '';
};

const sortActivitiesByOrder = (items) => {
    return [...(Array.isArray(items) ? items : [])].sort((a, b) => {
        const aOrderRaw = Number(a?.sort_order);
        const bOrderRaw = Number(b?.sort_order);
        const aOrder = Number.isFinite(aOrderRaw) ? aOrderRaw : Number.MAX_SAFE_INTEGER;
        const bOrder = Number.isFinite(bOrderRaw) ? bOrderRaw : Number.MAX_SAFE_INTEGER;

        if (aOrder !== bOrder) {
            return aOrder - bOrder;
        }

        const aCreatedAt = new Date(a?.created_at || 0).getTime();
        const bCreatedAt = new Date(b?.created_at || 0).getTime();
        return aCreatedAt - bCreatedAt;
    });
};

const LOCATION_EXCLUDED_KEYS = new Set(['latitude', 'longitude', 'lat', 'lng']);

const normalizeStandText = (value) => {
    if (typeof value === 'string') {
        return value.trim();
    }

    if (value && typeof value === 'object' && typeof value.value === 'string') {
        return value.value.trim();
    }

    if (value === null || value === undefined) {
        return '';
    }

    return String(value).trim();
};

const Event = () => {

    const { width } = useWindowDimensions();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [event, setEvent] = useState(null);
    const [loading, setLoading] = useState(false)
    const [loadingTypes, setLoadingTypes] = useState(false)
    const { eventId } = useParams();
    const [ticketTypes, setTicketTypes] = useState([]);
    const [ticketType, setTicketType] = useState(null);
    const [oldLogo, setOldLogo] = useState(null);
    const [oldStrip, setOldStrip] = useState(null);
    const [loadingTickets, setLoadingTickets] = useState(false);
    const [tickets, setTickets] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [per, setPer] = useState(25);
    const [q, setQ] = useState('');
    const [guestQ, setGuestQ] = useState('');
    const [activities, setActivities] = useState([]);
    const [loadingActivities, setLoadingActivities] = useState(false);
    const [activity, setActivity] = useState(null);
    const [activityTab, setActivityTab] = useState('information');
    const [savingActivity, setSavingActivity] = useState(false);
    const [savingActivitiesOrder, setSavingActivitiesOrder] = useState(false);
    const [orderingActivities, setOrderingActivities] = useState(false);
    const [activitiesOrderDraft, setActivitiesOrderDraft] = useState([]);
    const [guests, setGuests] = useState([]);
    const [loadingGuests, setLoadingGuests] = useState(false);
    const [guestCourses, setGuestCourses] = useState([]);
    const [guest, setGuest] = useState(null);
    const [savingGuest, setSavingGuest] = useState(false);
    const [deletingGuestId, setDeletingGuestId] = useState(null);
    const [stand, setStand] = useState(null);
    const [standTalentQr, setStandTalentQr] = useState(null);
    const [loadingStandTalentQr, setLoadingStandTalentQr] = useState(false);
    const [eventPartnerCompanies, setEventPartnerCompanies] = useState([]);
    const [filteredEventPartnerCompanies, setFilteredEventPartnerCompanies] = useState([]);
    const [savingStand, setSavingStand] = useState(false);
    const [deletingStandId, setDeletingStandId] = useState(null);
    const [savingGuestsOrder, setSavingGuestsOrder] = useState(false);
    const [standsOrderDraft, setStandsOrderDraft] = useState(null);
    const [savingStandsOrder, setSavingStandsOrder] = useState(false);
    const [standsOrderError, setStandsOrderError] = useState('');
    const saveStandsOrder = async () => {
        setSavingStandsOrder(true);
        setStandsOrderError('');
        try {
            const response = await api.put(`/admin/events/${eventId}/stands/reorder`, {
                ordered_ids: standsOrderDraft.map(item => item.id),
            });
            setEvent(previous => ({ ...previous, stands: response }));
            setStandsOrderDraft(null);
        } catch (error) {
            setStandsOrderError("Impossibile salvare l’ordine. Riprova.");
        } finally {
            setSavingStandsOrder(false);
        }
    };
    const [orderingGuests, setOrderingGuests] = useState(false);
    const [guestsOrderDraft, setGuestsOrderDraft] = useState([]);
    const [activitySlots, setActivitySlots] = useState([]);
    const [loadingActivitySlots, setLoadingActivitySlots] = useState(false);
    const [slotForm, setSlotForm] = useState(null);
    const [savingSlot, setSavingSlot] = useState(false);
    const [panelDelete, setPanelDelete] = useState(null);
    const [deletingPanel, setDeletingPanel] = useState(false);
    const [panelDeleteError, setPanelDeleteError] = useState('');
    const [panelFormError, setPanelFormError] = useState('');
    const [roomFormError, setRoomFormError] = useState('');
    const [selectedSlotDetails, setSelectedSlotDetails] = useState(null);
    const [slotRoomQr, setSlotRoomQr] = useState(null);
    const [slotBookings, setSlotBookings] = useState([]);
    const [loadingSlotBookings, setLoadingSlotBookings] = useState(false);
    const [selectedLists, setSelectedLists] = useState([]);
    const [lists, setLists] = useState([]);
    const [slotSelectedLists, setSlotSelectedLists] = useState([]);
    const [slotLists, setSlotLists] = useState([]);
    const [eventAnalytics, setEventAnalytics] = useState(null);
    const [loadingAnalytics, setLoadingAnalytics] = useState(false);
    const [eventLiveEntriesAnalytics, setEventLiveEntriesAnalytics] = useState(null);
    const [loadingEventLiveEntriesAnalytics, setLoadingEventLiveEntriesAnalytics] = useState(false);
    const [postTicketActivity, setPostTicketActivity] = useState({ leads: [], purchases: [] });
    const [loadingPostTicketActivity, setLoadingPostTicketActivity] = useState(false);
    const [eventNotificationStats, setEventNotificationStats] = useState({ reachable_users: 0, reachable_tokens: 0 });
    const [loadingEventNotificationStats, setLoadingEventNotificationStats] = useState(false);
    const [eventNotificationForm, setEventNotificationForm] = useState({ title: '', body: '', url: '', category: 'informational' });
    const [eventNotificationOpen, setEventNotificationOpen] = useState(false);
    const [sendingEventNotification, setSendingEventNotification] = useState(false);
    const [eventNotificationResult, setEventNotificationResult] = useState(null);
    const [eventNotificationError, setEventNotificationError] = useState('');
    const [qrScannerOpen, setQrScannerOpen] = useState(false);
    const [startingQrScanner, setStartingQrScanner] = useState(false);
    const [scannerPaused, setScannerPaused] = useState(false);
    const [scannerError, setScannerError] = useState('');
    const [scannerSuccess, setScannerSuccess] = useState('');
    const [videoInputDevices, setVideoInputDevices] = useState([]);
    const [selectedCameraId, setSelectedCameraId] = useState('');
    const [lastScannedCode, setLastScannedCode] = useState('');
    const [scanningTicket, setScanningTicket] = useState(false);
    const [scannedTicket, setScannedTicket] = useState(null);
    const [selectedTicket, setSelectedTicket] = useState(null);
    const [manualScanLoading, setManualScanLoading] = useState(false);
    const [manualScanError, setManualScanError] = useState('');
    const [manualScanSuccess, setManualScanSuccess] = useState('');
    const [manualScanConfirm, setManualScanConfirm] = useState({ open: false, code: null });
    const [geoDimension, setGeoDimension] = useState('city');
    const [utmDimension, setUtmDimension] = useState('source');
    const qrVideoRef = useRef(null);
    const qrScannerRef = useRef(null);
    const qrAutoResumeTimeoutRef = useRef(null);
    const scanningTicketRef = useRef(false);
    const [analyticsFilters, setAnalyticsFilters] = useState(() => {
        const minDate = getMinEventAnalyticsDate();
        const thirtyDaysAgo = addDays(new Date(), -30);
        thirtyDaysAgo.setHours(0, 0, 0, 0);

        const fromDateTimestamp = Math.max(thirtyDaysAgo.getTime(), minDate.getTime());
        const initialFromDate = new Date(fromDateTimestamp);

        const initialToDate = (() => {
            const now = new Date();
            now.setHours(23, 59, 59, 999);
            return now;
        })();

        return {
            from_date: initialFromDate,
            to_date: initialToDate,
        };
    });

    const accessLevels = [
        { id: 0, name: 'Standard', value: 'standard' },
        { id: 1, name: 'Stampa', value: 'press' },
        { id: 2, name: 'VIP', value: 'vip' },
        { id: 3, name: 'Staff', value: 'staff' },
        { id: 4, name: 'Partner', value: 'partner' },
        { id: 5, name: 'Speaker', value: 'speaker' },
        { id: 6, name: 'Sponsor', value: 'sponsor' },
        { id: 7, name: 'Ospite', value: 'guest' }
    ]

    const roomAccessTypeOptions = [
        { id: 0, name: 'Pubblica', value: 'public' },
        { id: 1, name: 'Mista', value: 'mixed' },
        { id: 2, name: 'Solo prenotabile', value: 'reservable_only' },
    ];

    const panelTypeOptions = [
        { id: 0, name: 'Panel', value: EventPanelType.Panel },
        { id: 1, name: 'Speech', value: EventPanelType.Speech },
        { id: 2, name: 'Interview', value: EventPanelType.Interview },
        { id: 3, name: 'Pause', value: EventPanelType.Pause },
        { id: 4, name: 'Opening', value: EventPanelType.Opening },
        { id: 5, name: 'Closing', value: EventPanelType.Closing },
    ];

    const reservableOptions = [
        { id: 0, name: 'Si', value: true },
        { id: 1, name: 'No', value: false },
    ];

    const geoDimensionOptions = [
        { id: 0, name: 'Città', value: 'city' },
        { id: 1, name: 'Provincia', value: 'province' },
        { id: 2, name: 'Regione', value: 'region' },
    ];

    const utmDimensionOptions = [
        { id: 0, name: 'Source', value: 'source' },
        { id: 1, name: 'Medium', value: 'medium' },
        { id: 2, name: 'Campaign', value: 'campaign' },
    ];

    const saveDisabled = useMemo(() => {
        if (!ticketType) return true;
        if (!ticketType.name ||
            ticketType.name.trim() === '' ||
            !ticketType.accessLevel ||
            ticketType.accessLevel.trim() === '' ||
            !ticketType.apple_pass?.icon ||
            !ticketType.apple_pass?.logo ||
            !ticketType.apple_pass?.["logo@2x"] ||
            !ticketType.apple_pass?.["logo@3x"] ||
            !ticketType.apple_pass?.strip ||
            !ticketType.google_pass?.strip ||
            !ticketType.google_pass?.icon ||
            !ticketType.google_pass?.logo ||
            !ticketType.apple_pass?.backgroundColor ||
            !ticketType.apple_pass?.foregroundColor ||
            !ticketType.google_pass?.backgroundColor ||
            !ticketType.google_pass?.foregroundColor
        ) return true;
        return false;
    }, [ticketType]);

    const saveActivityDisabled = useMemo(() => {
        if (!activity) return true;

        const name = activity?.name?.trim();
        const roomAccessType = activity?.room_access_type || 'mixed';
        const requiresReservationSettings = roomAccessType !== 'public';

        const slotCapacity = parseInt(activity?.slot_capacity, 10);
        const maxSlotsPerUser = parseInt(activity?.max_slots_per_user, 10);

        if (!name) return true;
        if (requiresReservationSettings && (Number.isNaN(slotCapacity) || slotCapacity <= 0)) return true;
        if (requiresReservationSettings && (Number.isNaN(maxSlotsPerUser) || maxSlotsPerUser <= 0)) return true;

        return false;
    }, [activity]);

    const saveSlotDisabled = useMemo(() => {
        if (!activity?.id || !slotForm) return true;

        const name = slotForm?.name?.trim();
        const startsAt = normalizeSlotDateValue(slotForm?.starts_at);
        const endsAt = normalizeSlotDateValue(slotForm?.ends_at);
        const capacity = parseInt(slotForm?.capacity, 10);
        const canConfigureReservation = activity?.room_access_type !== 'public';
        const isReservable = canConfigureReservation ? slotForm?.is_reservable !== false : false;

        const start = parseEventDate(startsAt);
        const end = parseEventDate(endsAt);
        if (!name || !start || !end || end < start) return true;
        if (isReservable && (Number.isNaN(capacity) || capacity <= 0)) return true;

        return false;
    }, [activity, slotForm]);

    const saveGuestDisabled = useMemo(() => {
        if (!guest) return true;

        const name = guest?.name?.trim();
        if (!name) return true;

        return false;
    }, [guest]);

    const saveStandDisabled = useMemo(() => {
        if (!stand) return true;

        const name = normalizeStandText(stand?.name);
        const companyName = normalizeStandText(stand?.company_name);
        const description = normalizeStandText(stand?.description);

        if (!name || !companyName || !description) return true;
        if (stand.stand_number !== '' && stand.stand_number != null && (!Number.isInteger(Number(stand.stand_number)) || Number(stand.stand_number) < 1 || Number(stand.stand_number) > 2147483647)) return true;

        return false;
    }, [stand]);

    const selectedStandCompany = useMemo(() => {
        if (!stand?.company_id) return null;
        return eventPartnerCompanies.find((company) => String(company.value) === String(stand.company_id)) || null;
    }, [eventPartnerCompanies, stand?.company_id]);

    const orderedGuests = useMemo(() => {
        return orderingGuests ? guestsOrderDraft : guests;
    }, [guests, guestsOrderDraft, orderingGuests]);

    const filteredGuests = useMemo(() => {
        const query = String(guestQ || '').trim().toLowerCase();

        if (!query) {
            return orderedGuests;
        }

        return orderedGuests.filter((item) => {
            const fullName = `${item?.name || ''} ${item?.surname || ''}`.trim().toLowerCase();
            const role = String(item?.role || '').toLowerCase();
            const company = String(item?.company || '').toLowerCase();
            const id = String(item?.id || '').toLowerCase();

            return fullName.includes(query) || role.includes(query) || company.includes(query) || id.includes(query);
        });
    }, [orderedGuests, guestQ]);

    const orderedActivities = useMemo(() => {
        if (orderingActivities) {
            return activitiesOrderDraft;
        }

        return sortActivitiesByOrder(activities);
    }, [activities, activitiesOrderDraft, orderingActivities]);

    const panelGuestOptions = useMemo(() => {
        return guests.map((item) => ({
            id: item.id,
            name: `${item.name || ''} ${item.surname || ''}`.trim() || `Ospite ${item.id}`,
        }));
    }, [guests]);

    const guestCourseOptions = useMemo(() => {
        return (Array.isArray(guestCourses) ? guestCourses : []).map((item) => ({
            id: item.id,
            name: item.name || `Corso ${item.id}`,
        }));
    }, [guestCourses]);

    const selectedGuestCourses = useMemo(() => {
        const courseIds = Array.isArray(guest?.course_ids) ? guest.course_ids : [];
        return guestCourseOptions.filter((option) => courseIds.includes(option.id));
    }, [guest?.course_ids, guestCourseOptions]);

    const selectedPanelGuests = useMemo(() => {
        const guestIds = Array.isArray(slotForm?.guest_ids) ? slotForm.guest_ids : [];
        return panelGuestOptions.filter((option) => guestIds.includes(option.id));
    }, [panelGuestOptions, slotForm?.guest_ids]);

    const selectedPanelModerators = useMemo(() => {
        const moderatorIds = Array.isArray(slotForm?.moderator_ids) ? slotForm.moderator_ids : [];
        return panelGuestOptions.filter((option) => moderatorIds.includes(option.id));
    }, [panelGuestOptions, slotForm?.moderator_ids]);

    const locationDetails = useMemo(() => {
        const location = event?.location;

        if (!location || typeof location !== 'object') {
            return [];
        }

        const hasValue = (value) => {
            if (value === null || value === undefined) {
                return false;
            }

            if (typeof value === 'string') {
                return value.trim() !== '';
            }

            return ['number', 'boolean'].includes(typeof value);
        };

        const details = [];
        const locationName = location?.name;
        const locationAddress = location?.address ?? location?.indirizzo;

        if (hasValue(locationName) && !LOCATION_EXCLUDED_KEYS.has('name')) {
            details.push({
                key: 'name',
                label: 'Nome',
                value: String(locationName),
            });
        }

        if (hasValue(locationAddress) && !LOCATION_EXCLUDED_KEYS.has('address')) {
            details.push({
                key: 'address',
                label: 'Indirizzo',
                value: String(locationAddress),
            });
        }

        return details;
    }, [event]);

    const analyticsDatePickerOptions = useMemo(
        () => ({
            css: [
                'https://cdn.jsdelivr.net/npm/@easepick/bundle@1.2.1/dist/index.css',
                'https://cdn.jsdelivr.net/npm/@easepick/range-plugin@1.2.1/dist/index.css',
                '/css/easepick-range.css',
            ],
            zIndex: 1000,
            plugins: [
                RangePlugin,
                PresetPlugin,
                AmpPlugin,
                LockPlugin
            ],
            lang: 'it-IT',
            format: "DD MMM YYYY",
            LockPlugin: {
                minDate: getMinEventAnalyticsDate()
            },
            AmpPlugin: {
                darkMode: false,
                dropdown: {
                    months: true,
                    years: true,
                    minYear: 2020
                }
            },
            PresetPlugin: {
                customPreset: {
                    'Oggi': [
                        (() => {
                            const date = new Date();
                            return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);
                        })(),
                        (() => {
                            const date = new Date();
                            return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);
                        })(),
                    ],
                    '7 giorni': [
                        (() => {
                            const date = new Date();
                            date.setDate(date.getDate() - 6);
                            return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);
                        })(),
                        (() => {
                            const date = new Date();
                            return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);
                        })(),
                    ],
                    '30 giorni': [
                        (() => {
                            const date = new Date();
                            date.setDate(date.getDate() - 29);
                            return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);
                        })(),
                        (() => {
                            const date = new Date();
                            return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);
                        })(),
                    ],
                    'Mese': [
                        (() => {
                            const now = new Date();
                            return new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);
                        })(),
                        (() => {
                            const now = new Date();
                            return new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
                        })(),
                    ],
                }
            },
            setup(picker) {
                picker.on("select", (e) => {
                    const { start, end } = e.detail;
                    const startDate = new Date(start.getTime());
                    startDate.setHours(0, 0, 0, 0);
                    const endDate = new Date(end.getTime());
                    endDate.setHours(23, 59, 59, 999);

                    setAnalyticsFilters((prev) => ({
                        ...prev,
                        from_date: startDate,
                        to_date: endDate,
                    }));
                });
            },
        }),
        []
    );


    const getTickets = useCallback(async () => {
        setLoadingTickets(true);
        try {
            let path = `/admin/events/${eventId}/tickets?page=${page}&per=${per}`
            if (q && q.trim() !== '') {
                path += `&q=${encodeURIComponent(q.replaceAll(' ', '').trim().toLowerCase())}`;
            }
            const response = await api.get(path);
            setTickets(response.tickets);
            setTotal(response.total);
        } catch (error) {
            console.error(error);
        }
        setLoadingTickets(false);
    }, [eventId, page, per, q]);

    const getEventNotificationStats = useCallback(async () => {
        if (!eventId) {
            setEventNotificationStats({ reachable_users: 0, reachable_tokens: 0 });
            return;
        }

        setLoadingEventNotificationStats(true);
        try {
            const category = eventNotificationForm.category || 'informational';
            const response = await api.get(`/admin/events/${eventId}/notifications/reachable?category=${encodeURIComponent(category)}`);
            setEventNotificationStats({
                reachable_users: Number(response?.reachable_users || 0),
                reachable_tokens: Number(response?.reachable_tokens || 0),
            });
        } catch (error) {
            console.error(error);
        }
        setLoadingEventNotificationStats(false);
    }, [eventId, eventNotificationForm.category]);

    const sendEventNotification = async () => {
        const title = eventNotificationForm.title.trim();
        const body = eventNotificationForm.body.trim();
        const url = eventNotificationForm.url.trim();
        const category = eventNotificationForm.category || 'informational';

        if (!title || !body || sendingEventNotification) {
            return;
        }

        const confirmed = window.confirm(`Inviare questa notifica ${category === 'marketing' ? 'marketing' : 'informativa'} a ${formatNumber(eventNotificationStats.reachable_users)} utenti raggiungibili?`);
        if (!confirmed) {
            return;
        }

        setSendingEventNotification(true);
        setEventNotificationResult(null);
        setEventNotificationError('');
        try {
            const response = await api.post(`/admin/events/${eventId}/notifications`, {
                title,
                body,
                url: url || null,
                category,
            });
            setEventNotificationResult(response);
            setEventNotificationForm((prev) => ({ ...prev, title: '', body: '', url: '' }));
            await getEventNotificationStats();
        } catch (error) {
            console.error(error);
            setEventNotificationError(error?.message || 'Invio notifica non riuscito.');
        }
        setSendingEventNotification(false);
    };

    const getActivities = useCallback(async () => {
        setLoadingActivities(true);
        try {
            const response = await api.get(`/admin/events/${eventId}/activities`);
            setActivities(sortActivitiesByOrder(Array.isArray(response) ? response : []));
        } catch (error) {
            console.error(error);
        }
        setLoadingActivities(false);
    }, [eventId]);

    const getGuests = useCallback(async () => {
        if (!eventId) {
            setGuests([]);
            return;
        }

        setLoadingGuests(true);
        try {
            const response = await api.get(`/admin/events/${eventId}/guests`);
            setGuests(Array.isArray(response) ? response : []);
        } catch (error) {
            console.error(error);
        }
        setLoadingGuests(false);
    }, [eventId]);

    const getGuestCourses = useCallback(async () => {
        setGuestCourses([]);
        try {
            const response = await api.get('/admin/courses?complete=false');
            setGuestCourses(Array.isArray(response) ? response : []);
        } catch (error) {
            console.error(error);
        }
    }, []);

    const getActivitySlots = useCallback(async (activityId) => {
        if (!eventId || !activityId) {
            setActivitySlots([]);
            return;
        }

        setLoadingActivitySlots(true);
        try {
            const response = await api.get(`/admin/events/${eventId}/activities/${activityId}/slots`);
            setActivitySlots(Array.isArray(response) ? response : []);
        } catch (error) {
            console.error(error);
        }
        setLoadingActivitySlots(false);
    }, [eventId]);

    const openSlotDetailsDialog = useCallback(async (slot) => {
        if (!eventId || !activity?.id || !slot?.id) {
            return;
        }

        setSelectedSlotDetails(slot);
        setLoadingSlotBookings(true);
        try {
            const response = await api.get(`/admin/events/${eventId}/activities/${activity.id}/slots/${slot.id}/bookings`);
            if (response?.slot) {
                setSelectedSlotDetails(current => current?.id === slot.id ? { ...current, ...response.slot } : current);
            }
            setSlotBookings(Array.isArray(response?.bookings) ? response.bookings : []);
        } catch (error) {
            console.error(error);
            setSlotBookings([]);
        }
        setLoadingSlotBookings(false);
    }, [activity?.id, eventId]);

    const loadActivityRoomQr = useCallback(async (activityId) => {
        if (!eventId || !activityId) {
            setSlotRoomQr(null);
            return;
        }

        try {
            const response = await api.get(`/admin/events/${eventId}/activities/${activityId}/room_qrcode`);
            setSlotRoomQr({
                token: response?.room_qr_token || '',
                code: response?.room_qr_code || '',
            });
        } catch (error) {
            console.error(error);
            setSlotRoomQr(null);
        }
    }, [eventId]);

    useEffect(() => {
        getTickets();
    }, [getTickets, page, per, q, eventId]);

    useEffect(() => {
        if (eventNotificationOpen) getEventNotificationStats();
    }, [eventNotificationOpen, getEventNotificationStats]);

    useEffect(() => {
        if (eventId) {
            getActivities();
        }
    }, [eventId, getActivities]);

    useEffect(() => {
        if (eventId) {
            getGuests();
        }
    }, [eventId, getGuests]);

    useEffect(() => {
        getGuestCourses();
    }, [getGuestCourses]);

    useEffect(() => {
        const getEventPartnerCompanies = async () => {
            try {
                const response = await api.get(`/admin/companies?page=0&per=500&company_type=${CompanyType.EventPartner}`);
                const companies = (response?.companies || [])
                    .map((company) => ({
                        id: company.id,
                        value: company.id,
                        name: company.name,
                        logo: company.logo || null,
                    }))
                    .sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), 'it'));
                setEventPartnerCompanies(companies);
                setFilteredEventPartnerCompanies(companies);
            } catch (error) {
                console.error(error);
            }
        };

        getEventPartnerCompanies();
    }, []);

    useEffect(() => {
        if (activity?.id) {
            getActivitySlots(activity.id);
        } else {
            setActivitySlots([]);
        }
    }, [activity?.id, getActivitySlots]);

    useEffect(() => {
        if (!activity?.id || !['mixed', 'reservable_only'].includes(activity.room_access_type)) {
            setSlotRoomQr(null);
            return;
        }

        loadActivityRoomQr(activity.id);
    }, [activity?.id, activity?.room_access_type, loadActivityRoomQr]);

    useEffect(() => {
        const getEventAnalytics = async () => {
            setLoadingAnalytics(true);
            try {
                const params = new URLSearchParams();
                const fromDateRome = formatDateAsRomeDay(analyticsFilters.from_date);
                const toDateRome = formatDateAsRomeDay(analyticsFilters.to_date);

                if (fromDateRome) {
                    params.append('created_from', fromDateRome);
                }
                if (toDateRome) {
                    params.append('created_to', toDateRome);
                }

                const suffix = params.toString() ? `?${params.toString()}` : '';
                const response = await api.get(`/admin/events/${eventId}/analytics${suffix}`);
                setEventAnalytics(response);
            } catch (error) {
                console.error(error);
            }
            setLoadingAnalytics(false);
        };

        if (eventId) {
            getEventAnalytics();
        }
    }, [eventId, analyticsFilters.from_date, analyticsFilters.to_date]);

    useEffect(() => {
        if (!eventId) {
            setPostTicketActivity({ leads: [], purchases: [] });
            return;
        }

        let active = true;

        const getPostTicketActivity = async () => {
            setLoadingPostTicketActivity(true);
            try {
                const response = await api.get(`/admin/events/${eventId}/post_ticket_activity`);
                if (!active) {
                    return;
                }

                setPostTicketActivity({
                    leads: Array.isArray(response?.leads) ? response.leads : [],
                    purchases: Array.isArray(response?.purchases) ? response.purchases : [],
                });
            } catch (error) {
                console.error(error);
            }
            if (active) {
                setLoadingPostTicketActivity(false);
            }
        };

        getPostTicketActivity();

        return () => {
            active = false;
        };
    }, [eventId]);

    useEffect(() => {
        if (!eventId) {
            setEventLiveEntriesAnalytics(null);
            return;
        }

        let mounted = true;
        let intervalId = null;

        const fetchEventLiveEntriesAnalytics = async () => {
            setLoadingEventLiveEntriesAnalytics(true);
            try {
                const response = await api.get(`/admin/events/${eventId}/live_entries_analytics`);
                if (!mounted) {
                    return;
                }

                setEventLiveEntriesAnalytics(response);

                if (intervalId) {
                    clearInterval(intervalId);
                    intervalId = null;
                }

                if (response?.is_live) {
                    intervalId = setInterval(() => {
                        fetchEventLiveEntriesAnalytics();
                    }, 60000);
                }
            } catch (error) {
                console.error(error);
            }
            if (mounted) {
                setLoadingEventLiveEntriesAnalytics(false);
            }
        };

        fetchEventLiveEntriesAnalytics();

        return () => {
            mounted = false;
            if (intervalId) {
                clearInterval(intervalId);
            }
        };
    }, [eventId]);

    const eventAnalyticsMetrics = useMemo(() => {
        const actual = eventAnalytics?.actual || {};
        const previous = eventAnalytics?.previous || {};

        return [
            {
                key: 'page_views',
                title: 'PAGE VIEWS',
                actual: Number(actual.page_views || 0),
                previous: Number(previous.page_views || 0),
            },
            {
                key: 'unique_users',
                title: 'UTENTI UNICI',
                actual: Number(actual.unique_users || 0),
                previous: Number(previous.unique_users || 0),
            },
            {
                key: 'sessions',
                title: 'SESSIONI',
                actual: Number(actual.sessions || 0),
                previous: Number(previous.sessions || 0),
            },
            {
                key: 'tickets_created',
                title: 'BIGLIETTI CREATI',
                actual: Number(actual.tickets_created || 0),
                previous: Number(previous.tickets_created || 0),
            },
        ];
    }, [eventAnalytics]);

    const geoDistributionRows = useMemo(() => {
        const items = eventAnalytics?.actual?.geo_distribution?.[geoDimension]?.items || [];
        return items.map((item, index) => ({
            rank: index + 1,
            value: item?.value || '—',
            count: Number(item?.count || 0),
            percentage: Number(item?.percentage || 0),
        }));
    }, [eventAnalytics, geoDimension]);

    const utmRows = useMemo(() => {
        const items = eventAnalytics?.actual?.utm?.[utmDimension] || [];
        return items.map((item, index) => ({
            rank: index + 1,
            value: item?.value || 'unknown',
            views: Number(item?.views || 0),
            tickets: Number(item?.tickets || 0),
            conversion_rate: Number(item?.conversion_rate || 0),
        }));
    }, [eventAnalytics, utmDimension]);

    const postTicketLeadRows = useMemo(() => {
        return (postTicketActivity.leads || []).map((lead) => {
            const fullName = [lead.user_name, lead.user_surname].filter(Boolean).join(' ').trim();
            return {
                id: lead.user_application_id,
                href: '/applications',
                user: fullName || '—',
                email: lead.user_email || '—',
                course: lead.course_name || lead.application_name || '—',
                status: {
                    value: lead.status || '',
                    type: 'string',
                    component: <StatusBadge appearance="soft" label={getUserApplicationStatusBadge(lead.status).name} color={getUserApplicationStatusBadge(lead.status).color} />,
                },
                application_created_at: lead.application_created_at,
            };
        });
    }, [postTicketActivity.leads]);

    const postTicketPurchaseRows = useMemo(() => {
        return (postTicketActivity.purchases || []).map((purchase) => {
            const fullName = [purchase.user_name, purchase.user_surname].filter(Boolean).join(' ').trim();
            const productAmount = Number(purchase.product_amount || 0);
            return {
                id: `${purchase.order_id}-${purchase.order_product_id}`,
                href: `/orders/${purchase.order_id}`,
                user: fullName || '—',
                email: purchase.user_email || '—',
                order_no: purchase.order_no || '—',
                product: purchase.product_name || '—',
                amount: {
                    value: productAmount,
                    type: 'number',
                    component: <span>{formatPrice(productAmount / 100)} €</span>,
                },
                purchased_at: purchase.purchased_at,
                ticket_created_at: purchase.ticket_created_at,
            };
        });
    }, [postTicketActivity.purchases]);

    const showLiveEntriesAnalytics = useMemo(() => {
        return Boolean(eventLiveEntriesAnalytics?.visible);
    }, [eventLiveEntriesAnalytics]);

    const liveEntriesStats = useMemo(() => {
        return {
            scannedTickets: Number(eventLiveEntriesAnalytics?.scanned_tickets || 0),
            attendanceRate: Number(eventLiveEntriesAnalytics?.attendance_rate || 0),
        };
    }, [eventLiveEntriesAnalytics]);

    const liveEntriesChartData = useMemo(() => {
        const items = Array.isArray(eventLiveEntriesAnalytics?.entries_by_hour)
            ? eventLiveEntriesAnalytics.entries_by_hour
            : [];

        return items.map((item) => ({
            label: item?.label || (item?.hour_start ? String(new Date(item.hour_start).getHours()) : ''),
            scanned_tickets: Number(item?.scanned_tickets || 0),
        }));
    }, [eventLiveEntriesAnalytics]);

    const scannedTicketDetails = useMemo(() => {
        if (!scannedTicket) {
            return null;
        }

        const ticketFromList = tickets.find((ticket) => ticket.id === scannedTicket.id);
        return ticketFromList || scannedTicket;
    }, [scannedTicket, tickets]);

    const selectedTicketDetails = useMemo(() => {
        if (!selectedTicket) {
            return null;
        }

        const ticketFromList = tickets.find((ticket) => ticket.id === selectedTicket.id);
        return ticketFromList || selectedTicket;
    }, [selectedTicket, tickets]);

    const getLatestScan = useCallback((ticket) => {
        const scans = Array.isArray(ticket?.scans) ? ticket.scans : [];
        if (scans.length === 0) {
            return null;
        }

        const sortedScans = [...scans].sort((a, b) => {
            const aTime = a?.scanned_at ? new Date(a.scanned_at).getTime() : 0;
            const bTime = b?.scanned_at ? new Date(b.scanned_at).getTime() : 0;
            return bTime - aTime;
        });

        return sortedScans[0] || null;
    }, []);

    const getTicketActivityBookings = useCallback((ticket) => {
        const bookings = Array.isArray(ticket?.activity_bookings) ? ticket.activity_bookings : [];
        return [...bookings].sort((a, b) => {
            const aTime = a?.slot?.starts_at ? new Date(a.slot.starts_at).getTime() : Number.MAX_SAFE_INTEGER;
            const bTime = b?.slot?.starts_at ? new Date(b.slot.starts_at).getTime() : Number.MAX_SAFE_INTEGER;
            return aTime - bTime;
        });
    }, []);

    const getActivityBookingTimeRange = useCallback((booking) => {
        const startsAt = booking?.slot?.starts_at;
        const endsAt = booking?.slot?.ends_at;

        if (!startsAt || !endsAt) {
            return 'Orario non disponibile';
        }

        return `${formatTimeV2(startsAt)} - ${formatTimeV2(endsAt)}`;
    }, []);

    const getActivityBookingMeta = useCallback((booking) => {
        const activityName = booking?.activity?.name || 'Attivita';
        const slotName = booking?.slot?.name || 'Slot';

        const normalizePeople = (value) => {
            if (!value) {
                return [];
            }

            if (Array.isArray(value)) {
                return value
                    .map((item) => {
                        if (typeof item === 'string') {
                            return item.trim();
                        }

                        if (item && typeof item === 'object') {
                            const fullName = `${item.name || ''} ${item.surname || ''}`.trim();
                            return fullName || item.full_name || item.label || '';
                        }

                        return '';
                    })
                    .filter((name) => !!name);
            }

            if (typeof value === 'string') {
                return value
                    .split(',')
                    .map((item) => item.trim())
                    .filter((name) => !!name);
            }

            return [];
        };

        const guests = normalizePeople(
            booking?.slot?.guests
            || booking?.slot?.guest_names
            || booking?.slot?.guest_list
        );

        const moderators = normalizePeople(
            booking?.slot?.moderators
            || booking?.slot?.moderator_names
            || booking?.slot?.moderator_list
        );

        return (
            <>
                <div>{`${activityName} • ${slotName}`}</div>
                {guests.length > 0 && (
                    <div className={styles.ticketActivityPeopleSection}>
                        <div className={styles.ticketActivityPeopleLabel}>Ospiti</div>
                        <ul className={styles.ticketActivityPeopleList}>
                            {guests.map((guestName, index) => (
                                <li className={styles.ticketActivityPeopleItem} key={`guest-${booking?.id || slotName}-${index}`}>
                                    {guestName}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
                {moderators.length > 0 && (
                    <div className={styles.ticketActivityPeopleSection}>
                        <div className={styles.ticketActivityPeopleLabel}>Moderatori</div>
                        <ul className={styles.ticketActivityPeopleList}>
                            {moderators.map((moderatorName, index) => (
                                <li className={styles.ticketActivityPeopleItem} key={`moderator-${booking?.id || slotName}-${index}`}>
                                    {moderatorName}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </>
        );
    }, []);

    useEffect(() => {

        const getEvent = async () => {
            setLoading(true);
            try {
                const event = await api.get(`/admin/events/${eventId}`);
                setEvent(event);
            } catch (error) {
                console.error(error);
            }
            setLoading(false)
        }

        const getTicketTypes = async () => {
            setLoadingTypes(true);
            try {
                const types = await api.get(`/admin/events/${eventId}/tickets_types`);
                setTicketTypes(types);
            } catch (error) {
                console.error(error);
            }
            setLoadingTypes(false)
        }

        if (eventId && !event) {
            getEvent();
            getTicketTypes();
        }

    }, [eventId, getTickets, event]);

    const createTicketType = async () => {
        try {
            const body = {
                name: ticketType.name,
                event_id: event.id,
                price: isNaN(parseFloat(ticketType.price)) ? null : parseFloat(ticketType.price),
                max_quantity: ticketType.maxQuantity ? parseInt(ticketType.maxQuantity) : null,
                access_level: ticketType.accessLevel,
                description: ticketType.description,
                apple_pass: ticketType.apple_pass,
                google_pass: { ...ticketType.google_pass, force_update: true, class_created: false, version: ticketType.google_pass.version ? ticketType.google_pass.version + 1 : 1 },
                ac_lists: selectedLists.map(list => parseInt(list.id)),
                purchasable: ticketType.purchasable || false,
            }
            const newTicketType = await api.post(`/admin/tickets_types`, body);
            setTicketType(null);
            setTicketTypes([...ticketTypes, newTicketType]);
        }
        catch (error) {
            console.error(error);
        }
    }

    const updateTicketType = async (id) => {
        try {
            const version = oldLogo !== ticketType.google_pass?.logo || oldStrip !== ticketType.google_pass?.strip ? (ticketType.google_pass.version ? ticketType.google_pass.version + 1 : 1) : ticketType.google_pass.version;
            const body = {
                name: ticketType.name,
                event_id: event.id,
                price: isNaN(parseFloat(ticketType.price)) ? null : parseFloat(ticketType.price),
                max_quantity: ticketType.maxQuantity ? parseInt(ticketType.maxQuantity) : null,
                access_level: ticketType.accessLevel,
                description: ticketType.description,
                apple_pass: ticketType.apple_pass,
                google_pass: { ...ticketType.google_pass, force_update: true, class_created: true, version: version },
                ac_lists: selectedLists.map(list => parseInt(list.id)),
                purchasable: ticketType.purchasable || false,
            }
            const newTicketType = await api.put(`/admin/tickets_types/${id}`, body);
            setTicketType(null);
            setTicketTypes((prev) => {
                const index = prev.findIndex((type) => type.id === id);
                if (index === -1) return prev;
                const updated = [...prev];
                updated[index] = newTicketType;
                return updated;
            });
        }
        catch (error) {
            console.error(error);
        }
    }

    const saveTicketType = async () => {
        setSelectedLists([]);
        setLists([]);
        if (ticketType.id) {
            await updateTicketType(ticketType.id);
        } else {
            await createTicketType();
        }
    }

    const getStatusColor = (status) => {
        switch (status) {
            case 'public':
                return 'var(--sf-green)'
            case 'draft':
                return 'var(--secondary)'
            case 'hidden':
                return 'var(--sf-red)'
            default:
                return 'var(--sf-grey)'
        }
    }

    useEffect(() => {
        if (ticketType) {
            getListsByIds(ticketType.ac_lists ?? [])
            if (oldLogo !== ticketType.google_pass?.logo) {
                setOldLogo(ticketType.google_pass?.logo);
            }
            if (oldStrip !== ticketType.google_pass?.strip) {
                setOldStrip(ticketType.google_pass?.strip);
            }
        }
        else {
            setOldLogo(null);
            setOldStrip(null);
            setSelectedLists([]);
            setLists([]);
        }
    }, [ticketType]);

    function getLuminance(hex) {
        const rgb = hex.replace("#", "").match(/.{2}/g).map(c => parseInt(c, 16) / 255);

        const [r, g, b] = rgb.map(c => {
            return c <= 0.03928
                ? c / 12.92
                : Math.pow((c + 0.055) / 1.055, 2.4);
        });

        return 0.2126 * r + 0.7152 * g + 0.0722 * b;
    }

    function getTextColor(bgColor) {
        if (!bgColor) return "#000000";
        const luminance = getLuminance(bgColor);
        return luminance > 0.5 ? "#000000" : "#FFFFFF";
    }

    const getTicketStatusBadge = (status) => {
        switch (status) {
            case 'active':
                return <StatusBadge appearance="soft" label={t('events.tickets.types.active')} color="var(--sf-green)" />
            case 'used':
                return <StatusBadge appearance="soft" label={t('events.tickets.types.used')} color="var(--sf-blue)" />
            case 'pending':
                return <StatusBadge appearance="soft" label={t('events.tickets.types.pending')} color="var(--sf-yellow)" />
            case 'expired':
                return <StatusBadge appearance="soft" label={t('events.tickets.types.expired')} color="var(--sf-red)" />
            case 'revoked':
                return <StatusBadge appearance="soft" label={t('events.tickets.types.revoked')} color="black" />
            default:
                return <StatusBadge appearance="soft" label={status} color="var(--sf-grey)" />
        }
    }

    const searchLists = async (value) => {
        try {
            const lists = await api.get(`/admin/active-campaign/lists?q=${encodeURIComponent(value)}`);
            // sostituisci tutte le liste tranne quelle selezionate
            let newLists = lists.filter(list => !selectedLists.map(sl => parseInt(sl.id)).includes(parseInt(list.id)));
            setLists([...selectedLists, ...newLists]);
        }
        catch (e) {
            console.error("Error fetching lists:", e);
        }
    }

    const getListsByIds = async (ids) => {
        try {
            const lists = await api.get(`/admin/active-campaign/lists?ids=${encodeURIComponent(ids.join(','))}`);
            setLists(lists);
            setSelectedLists(lists.map(list => ({ id: parseInt(list.id), name: list.name })));
        }
        catch (e) {
            console.error("Error fetching lists by ids:", e);
        }
    }

    const searchSlotLists = async (value) => {
        try {
            const lists = await api.get(`/admin/active-campaign/lists?q=${encodeURIComponent(value)}`);
            let newLists = lists.filter(list => !slotSelectedLists.map(sl => parseInt(sl.id)).includes(parseInt(list.id)));
            setSlotLists([...slotSelectedLists, ...newLists]);
        }
        catch (e) {
            console.error("Error fetching slot lists:", e);
        }
    }

    const getSlotListsByIds = async (ids) => {
        if (!ids || ids.length === 0) {
            setSlotLists([]);
            setSlotSelectedLists([]);
            return;
        }

        try {
            const lists = await api.get(`/admin/active-campaign/lists?ids=${encodeURIComponent(ids.join(','))}`);
            setSlotLists(lists);
            setSlotSelectedLists(lists.map(list => ({ id: parseInt(list.id), name: list.name })));
        }
        catch (e) {
            console.error("Error fetching slot lists by ids:", e);
        }
    }

    const [exporting, setExporting] = useState(false)
    const [exportingList, setExportingList] = useState(false)
    const [exportingActivityId, setExportingActivityId] = useState(null)

    const sanitizeCsvNameValue = useCallback((value) => {
        if (value === null || value === undefined) {
            return '';
        }

        return String(value).replace(/,/g, ' ').replace(/\r?\n|\r/g, ' ').trim();
    }, []);

    const capitalizeNameValue = useCallback((value) => {
        return sanitizeCsvNameValue(value)
            .toLocaleLowerCase('it-IT')
            .replace(/(^|[\s'-])([a-zà-ÿ])/gi, (match, separator, letter) => (
                `${separator}${letter.toLocaleUpperCase('it-IT')}`
            ));
    }, [sanitizeCsvNameValue]);

    const escapeCsvValue = useCallback((value) => {
        if (value === null || value === undefined) {
            return '';
        }

        const normalized = String(value).replace(/\r?\n|\r/g, ' ').trim();
        return `"${normalized.replace(/"/g, '""')}"`;
    }, []);

    const sanitizeFileName = useCallback((value, fallback = 'activity') => {
        const raw = typeof value === 'string' ? value.trim() : '';
        const safe = raw
            .replace(/[^a-zA-Z0-9-_]+/g, '_')
            .replace(/_+/g, '_')
            .replace(/^_+|_+$/g, '');

        return safe || fallback;
    }, []);

    const translateScanErrorMessage = useCallback((message) => {
        const normalizedMessage = typeof message === 'string' ? message.trim() : '';
        const normalizedKey = normalizedMessage.toLowerCase().replace(/\.+$/, '');

        const errorMap = {
            'ticket not found': 'events.errors.ticketNotFound',
            'invalid ticket code': 'events.errors.invalidTicketCode',
            'ticket is expired': 'events.errors.ticketExpired',
            'ticket has been revoked': 'events.errors.ticketRevoked',
            'ticket has not been paid': 'events.errors.ticketNotPaid',
            'ticket does not belong to selected event': 'events.errors.ticketWrongEvent',
            'ticket can be scanned only between the event start day and end day': 'events.errors.scanOutsideEventDays',
            'generic error': 'events.errors.generic',
        };

        const translationKey = errorMap[normalizedKey];
        if (translationKey) {
            return t(translationKey);
        }

        return normalizedMessage || t('events.errors.generic');
    }, [t]);

    const clearQrAutoResumeTimeout = useCallback(() => {
        if (qrAutoResumeTimeoutRef.current) {
            clearTimeout(qrAutoResumeTimeoutRef.current);
            qrAutoResumeTimeoutRef.current = null;
        }
    }, []);

    const stopQrScanner = useCallback(() => {
        if (qrScannerRef.current) {
            qrScannerRef.current.stop();
            qrScannerRef.current.destroy();
            qrScannerRef.current = null;
        }

        if (qrVideoRef.current) {
            qrVideoRef.current.pause();
            qrVideoRef.current.srcObject = null;
        }
    }, []);

    const loadVideoInputDevices = useCallback(async () => {
        try {
            const cameras = await QrScanner.listCameras(true);
            const videoDevices = cameras.map((camera) => ({
                deviceId: camera.id,
                label: camera.label || '',
            }));

            setVideoInputDevices(videoDevices);
            setSelectedCameraId((prevSelected) => {
                if (prevSelected && videoDevices.some((device) => device.deviceId === prevSelected)) {
                    return prevSelected;
                }

                const rearCamera = videoDevices.find((device) => {
                    const label = (device.label || '').toLowerCase();
                    return label.includes('back') || label.includes('rear') || label.includes('environment') || label.includes('posteriore');
                });

                return rearCamera?.deviceId || videoDevices[0]?.deviceId || '';
            });
        } catch (error) {
            console.error('Impossibile ottenere la lista delle fotocamere', error);
        }
    }, []);

    const performTicketScan = useCallback(async (code) => {
        try {
            const ticket = await api.get(`/admin/tickets/scan/${encodeURIComponent(code)}?event_id=${eventId}`);
            return ticket;
        } catch (error) {
            const responseData = error?.response?.data || error;
            const responseDetail = responseData?.detail;

            let detailMessage = null;

            if (typeof responseDetail === 'string' && responseDetail.trim() !== '') {
                detailMessage = responseDetail;
            } else if (responseDetail && typeof responseDetail === 'object') {
                if (typeof responseDetail.detail === 'string' && responseDetail.detail.trim() !== '') {
                    detailMessage = responseDetail.detail;
                } else if (typeof responseDetail.message === 'string' && responseDetail.message.trim() !== '') {
                    detailMessage = responseDetail.message;
                }
            }

            if (!detailMessage && typeof responseData?.message === 'string' && responseData.message.trim() !== '') {
                detailMessage = responseData.message;
            }

            if (!detailMessage && typeof error?.detail === 'string' && error.detail.trim() !== '') {
                detailMessage = error.detail;
            } else if (!detailMessage && error?.detail && typeof error.detail === 'object') {
                if (typeof error.detail.detail === 'string' && error.detail.detail.trim() !== '') {
                    detailMessage = error.detail.detail;
                } else if (typeof error.detail.message === 'string' && error.detail.message.trim() !== '') {
                    detailMessage = error.detail.message;
                }
            }

            if (!detailMessage && typeof error?.message === 'string' && error.message.trim() !== '') {
                detailMessage = error.message;
            }

            throw new Error(translateScanErrorMessage(detailMessage));
        }
    }, [eventId, translateScanErrorMessage]);

    const mergeScannedTicketInState = useCallback((ticket) => {
        if (!ticket?.id) {
            return;
        }

        setTickets((previousTickets) => {
            if (!Array.isArray(previousTickets) || previousTickets.length === 0) {
                return previousTickets;
            }

            let updated = false;
            const nextTickets = previousTickets.map((currentTicket) => {
                if (currentTicket.id !== ticket.id) {
                    return currentTicket;
                }

                updated = true;
                return {
                    ...currentTicket,
                    ...ticket,
                };
            });

            return updated ? nextTickets : previousTickets;
        });

        setSelectedTicket((previousSelectedTicket) => {
            if (!previousSelectedTicket || previousSelectedTicket.id !== ticket.id) {
                return previousSelectedTicket;
            }

            return {
                ...previousSelectedTicket,
                ...ticket,
            };
        });
    }, []);

    const scanTicketCode = useCallback(async (code) => {
        if (!code || scanningTicketRef.current) {
            return;
        }

        scanningTicketRef.current = true;
        setScanningTicket(true);
        setScannerError('');
        setScannerSuccess('');
        clearQrAutoResumeTimeout();

        try {
            const ticket = await performTicketScan(code);
            setLastScannedCode(code);
            setScannedTicket(ticket);
            mergeScannedTicketInState(ticket);
            setScannerSuccess('Biglietto scansionato con successo');
            setScannerPaused(true);

            qrAutoResumeTimeoutRef.current = setTimeout(() => {
                setScannerSuccess('');
                setScannerPaused(false);
                qrAutoResumeTimeoutRef.current = null;
            }, SCAN_SUCCESS_RESUME_DELAY_MS);
        } catch (error) {
            setScannerError(error.message || 'Errore durante la scansione del ticket');
            setScannerPaused(true);
            qrAutoResumeTimeoutRef.current = setTimeout(() => {
                setScannerError('');
                setScannerPaused(false);
                qrAutoResumeTimeoutRef.current = null;
            }, SCAN_ERROR_RESUME_DELAY_MS);
        }

        setScanningTicket(false);
        scanningTicketRef.current = false;
    }, [clearQrAutoResumeTimeout, mergeScannedTicketInState, performTicketScan]);

    const runManualTicketScan = useCallback(async () => {
        if (!selectedTicketDetails?.qr_token || manualScanLoading) {
            return;
        }

        setManualScanLoading(true);
        setManualScanError('');
        setManualScanSuccess('');

        try {
            const scanned = await performTicketScan(selectedTicketDetails.qr_token);
            setManualScanSuccess('Biglietto impostato come scansionato');
            setSelectedTicket((prev) => ({ ...(prev || {}), ...scanned }));
            setScannedTicket(scanned);
            mergeScannedTicketInState(scanned);
            setLastScannedCode(selectedTicketDetails.qr_token);
        } catch (error) {
            setManualScanError(error.message || 'Errore durante la scansione del ticket');
        }

        setManualScanLoading(false);
    }, [manualScanLoading, mergeScannedTicketInState, performTicketScan, selectedTicketDetails?.qr_token]);

    const markTicketAsScannedNow = useCallback(() => {
        if (!selectedTicketDetails?.qr_token || manualScanLoading) {
            return;
        }

        setManualScanConfirm({
            open: true,
            code: selectedTicketDetails.qr_token,
        });
    }, [manualScanLoading, selectedTicketDetails?.qr_token]);

    const startQrScanner = useCallback(async () => {
        if (!qrScannerOpen || scannerPaused || !qrVideoRef.current) {
            return;
        }

        setStartingQrScanner(true);
        setScannerError('');

        try {
            const preferredCamera = selectedCameraId || 'environment';

            qrScannerRef.current = new QrScanner(
                qrVideoRef.current,
                async (scanResult) => {
                    const qrCode = typeof scanResult === 'string' ? scanResult : scanResult?.data;
                    if (!qrCode || scanningTicketRef.current || scannerPaused) {
                        return;
                    }

                    await scanTicketCode(qrCode);
                },
                {
                    preferredCamera,
                    maxScansPerSecond: 5,
                    returnDetailedScanResult: true,
                }
            );

            await qrScannerRef.current.start();
            await loadVideoInputDevices();
        } catch (error) {
            const message = typeof error?.message === 'string' ? error.message : '';
            const isPlayInterruptedError =
                error?.name === 'AbortError'
                || message.toLowerCase().includes('play() request was interrupted by a new load request');

            if (isPlayInterruptedError) {
                return;
            }

            const detail = error?.message || 'Impossibile avviare la fotocamera';
            setScannerError(detail);
            stopQrScanner();
        }

        setStartingQrScanner(false);
    }, [loadVideoInputDevices, qrScannerOpen, scanTicketCode, scannerPaused, selectedCameraId, stopQrScanner]);

    useEffect(() => {
        if (qrScannerOpen) {
            startQrScanner();
        } else {
            clearQrAutoResumeTimeout();
        }

        return () => {
            stopQrScanner();
        };
    }, [clearQrAutoResumeTimeout, qrScannerOpen, scannerPaused, startQrScanner, stopQrScanner]);

    useEffect(() => {
        return () => {
            clearQrAutoResumeTimeout();
        };
    }, [clearQrAutoResumeTimeout]);

    const exportTickets = async () => {
        setExporting(true)
        try {
            const blob = await api.get(`/admin/events/${eventId}/tickets/csv`, {
                timeout: 0,
                responseType: 'blob',
            })

            const url = window.URL.createObjectURL(blob)
            const link = document.createElement('a')
            link.href = url
            link.setAttribute('download', `event_${eventId}_tickets.csv`)
            document.body.appendChild(link)
            link.click()
            link.remove()
            window.URL.revokeObjectURL(url)

        }
        catch (e) {
            console.error(e)
        }
        setExporting(false)
    }

    const exportTicketsList = async () => {
        if (exportingList) {
            return;
        }

        setExportingList(true);

        try {
            const exportPer = 500;
            let exportPage = 0;
            let allTickets = [];
            let expectedTotal = null;

            while (expectedTotal === null || allTickets.length < expectedTotal) {
                const response = await api.get(`/admin/events/${eventId}/tickets?page=${exportPage}&per=${exportPer}`);
                const pageTickets = Array.isArray(response?.tickets) ? response.tickets : [];

                allTickets = [...allTickets, ...pageTickets];
                const responseTotal = Number(response?.total);
                expectedTotal = Number.isFinite(responseTotal) ? responseTotal : allTickets.length;

                if (pageTickets.length === 0) {
                    break;
                }

                exportPage += 1;
            }

            const sortedTickets = allTickets.sort((a, b) => {
                const aSurname = capitalizeNameValue(a?.user?.surname).toLocaleLowerCase('it-IT');
                const bSurname = capitalizeNameValue(b?.user?.surname).toLocaleLowerCase('it-IT');
                const surnameCompare = aSurname.localeCompare(bSurname, 'it-IT');

                if (surnameCompare !== 0) {
                    return surnameCompare;
                }

                const aName = capitalizeNameValue(a?.user?.name).toLocaleLowerCase('it-IT');
                const bName = capitalizeNameValue(b?.user?.name).toLocaleLowerCase('it-IT');
                return aName.localeCompare(bName, 'it-IT');
            });

            const header = ['Cognome', 'Nome', 'Mail', 'Codice del biglietto', 'Ingresso'];
            const rows = sortedTickets.map((ticket) => [
                capitalizeNameValue(ticket?.user?.surname),
                capitalizeNameValue(ticket?.user?.name),
                ticket?.user?.email || '',
                ticket?.qr_token || '',
                '',
            ]);

            const csvLines = [
                header.map(escapeCsvValue).join(','),
                ...rows.map((row) => row.map(escapeCsvValue).join(',')),
            ];

            const csvContent = `\uFEFF${csvLines.join('\n')}`;
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `event_${eventId}_lista_biglietti.csv`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        }
        catch (e) {
            console.error(e);
        }
        finally {
            setExportingList(false);
        }
    }

    const exportActivityParticipants = useCallback(async (activityItem) => {
        if (!eventId || !activityItem?.id || exportingActivityId) {
            return;
        }

        setExportingActivityId(activityItem.id);

        try {
            const slotsResponse = await api.get(`/admin/events/${eventId}/activities/${activityItem.id}/slots`);
            const slots = Array.isArray(slotsResponse) ? slotsResponse : [];

            if (slots.length === 0) {
                const emptyCsv = '\uFEFF"attivita_id","attivita_nome","slot_id","slot_nome","slot_inizio","slot_fine","booking_id","nome","cognome","email","professione","settore","anni_esperienza","motivazione","iscritto_il"\n';
                const emptyBlob = new Blob([emptyCsv], { type: 'text/csv;charset=utf-8;' });
                const emptyUrl = window.URL.createObjectURL(emptyBlob);
                const emptyLink = document.createElement('a');
                emptyLink.href = emptyUrl;
                emptyLink.setAttribute('download', `event_${eventId}_activity_${sanitizeFileName(activityItem.name)}_participants.csv`);
                document.body.appendChild(emptyLink);
                emptyLink.click();
                emptyLink.remove();
                window.URL.revokeObjectURL(emptyUrl);
                return;
            }

            const bookingsBySlot = await Promise.all(
                slots.map(async (slot) => {
                    try {
                        const response = await api.get(`/admin/events/${eventId}/activities/${activityItem.id}/slots/${slot.id}/bookings`);
                        return {
                            slot,
                            bookings: Array.isArray(response?.bookings) ? response.bookings : [],
                        };
                    } catch (error) {
                        console.error(`Errore nel recupero iscritti per lo slot ${slot?.id}`, error);
                        return {
                            slot,
                            bookings: [],
                        };
                    }
                })
            );

            const header = [
                'attivita_id',
                'attivita_nome',
                'slot_id',
                'slot_nome',
                'slot_inizio',
                'slot_fine',
                'booking_id',
                'nome',
                'cognome',
                'email',
                'professione',
                'settore',
                'anni_esperienza',
                'motivazione',
                'iscritto_il',
            ];

            const rows = [];
            bookingsBySlot.forEach(({ slot, bookings }) => {
                bookings.forEach((booking) => {
                    rows.push([
                        activityItem.id,
                        activityItem.name || '',
                        slot?.id || '',
                        slot?.name || '',
                        slot?.starts_at || '',
                        slot?.ends_at || '',
                        booking?.id || '',
                        booking?.name || '',
                        booking?.surname || '',
                        booking?.email || '',
                        booking?.current_profession || '',
                        booking?.sector || '',
                        booking?.experience_years || '',
                        booking?.motivation || '',
                        booking?.created_at || '',
                    ]);
                });
            });

            const csvLines = [
                header.map(escapeCsvValue).join(','),
                ...rows.map((row) => row.map(escapeCsvValue).join(',')),
            ];

            const csvContent = `\uFEFF${csvLines.join('\n')}`;
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `event_${eventId}_activity_${sanitizeFileName(activityItem.name)}_participants.csv`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        }
        catch (error) {
            console.error(error);
        }
        finally {
            setExportingActivityId(null);
        }
    }, [escapeCsvValue, eventId, exportingActivityId, sanitizeFileName]);

    const openQrScanner = useCallback(() => {
        setScannedTicket(null);
        setLastScannedCode('');
        setScannerError('');
        setScannerSuccess('');
        setScannerPaused(false);
        setQrScannerOpen(true);
        loadVideoInputDevices();
    }, [loadVideoInputDevices]);

    const downloadRoomQrPng = useCallback(() => {
        const qrCodeBase64 = String(slotRoomQr?.code || '').trim();
        if (!qrCodeBase64) {
            return;
        }

        const fileToken = slotRoomQr?.token || `room_${activity?.id || 'activity'}`;
        const safeToken = String(fileToken).replace(/[^a-zA-Z0-9_-]/g, '_');
        const link = document.createElement('a');
        link.href = `data:image/png;base64,${qrCodeBase64}`;
        link.setAttribute('download', `room_qr_${safeToken}.png`);
        document.body.appendChild(link);
        link.click();
        link.remove();
    }, [activity?.id, slotRoomQr?.code, slotRoomQr?.token]);

    const saveActivity = async () => {
        if (!eventId || !activity) {
            return;
        }

        setSavingActivity(true);
        setRoomFormError('');
        try {
            const parsedSlotCapacity = parseInt(activity.slot_capacity, 10);
            const parsedMaxSlotsPerUser = parseInt(activity.max_slots_per_user, 10);

            const body = {
                name: activity.name?.trim(),
                description: activity.description?.trim() || null,
                slot_capacity: Number.isNaN(parsedSlotCapacity) ? 60 : parsedSlotCapacity,
                max_slots_per_user: Number.isNaN(parsedMaxSlotsPerUser) ? 1 : parsedMaxSlotsPerUser,
                room_access_type: activity.room_access_type || 'mixed',
                sort_order: Number.isNaN(parseInt(activity?.sort_order, 10)) ? (activities.length + 1) : parseInt(activity?.sort_order, 10),
            };

            if (activity?.id) {
                await api.put(`/admin/events/${eventId}/activities/${activity.id}`, body);
            } else {
                await api.post(`/admin/events/${eventId}/activities`, body);
            }
            setActivity(null);
            await getActivities();
        } catch (error) {
            console.error(error);
            setRoomFormError(error?.message || "Impossibile salvare la stanza. Riprova.");
        }
        setSavingActivity(false);
    }

    const handleActivitiesSort = useCallback((rows) => {
        // Keep original records: table rows contain display-only values.
        setActivitiesOrderDraft(previous => {
            const byId = new Map(previous.map(item => [item.id, item]));
            return rows.map((row, index) => ({ ...byId.get(row.id), sort_order: index + 1 }));
        });
    }, []);

    const startActivitiesOrdering = useCallback(() => {
        if (activities.length <= 1) {
            return;
        }

        const base = sortActivitiesByOrder(activities).map((item, index) => ({
            ...item,
            sort_order: index + 1,
        }));

        setActivitiesOrderDraft(base);
        setOrderingActivities(true);
    }, [activities]);

    const cancelActivitiesOrdering = useCallback(() => {
        setOrderingActivities(false);
        setActivitiesOrderDraft([]);
    }, []);

    const saveActivitiesOrder = useCallback(async () => {
        if (!eventId || activitiesOrderDraft.length === 0) {
            return;
        }

        setSavingActivitiesOrder(true);
        try {
            await api.put(`/admin/events/${eventId}/activities/reorder`, {
                ordered_ids: activitiesOrderDraft.map((item) => item.id),
            });

            await getActivities();
            setOrderingActivities(false);
            setActivitiesOrderDraft([]);
        } catch (error) {
            console.error(error);
        }
        setSavingActivitiesOrder(false);
    }, [activitiesOrderDraft, eventId, getActivities]);

    useEffect(() => { setRoomFormError(''); setActivityTab('information'); }, [activity?.id]);
    useEffect(() => { setPanelFormError(''); }, [slotForm?.id]);

    const editPanel = (slot) => {
        setPanelFormError('');
        setSelectedSlotDetails(null);
        setSlotBookings([]);
        setSlotForm({
            ...slot,
            name: slot.name || '', description: slot.description || '',
            ac_lists: Array.isArray(slot.ac_lists) ? slot.ac_lists : [],
            guest_ids: Array.isArray(slot.guest_ids) ? slot.guest_ids : [],
            moderator_ids: Array.isArray(slot.moderator_ids) ? slot.moderator_ids : [],
            panel_type: slot.panel_type || 'panel',
            is_reservable: activity?.room_access_type === 'public' ? false : slot.is_reservable !== false,
        });
        getSlotListsByIds(Array.isArray(slot.ac_lists) ? slot.ac_lists : []);
    };

    const requestPanelDelete = (slot) => {
        setPanelDeleteError('');
        setPanelDelete({ slot, activityId: activity.id });
    };

    const deletePanel = async () => {
        if (!panelDelete || deletingPanel) return;
        setDeletingPanel(true);
        setPanelDeleteError('');
        const { slot, activityId } = panelDelete;
        try {
            await api.delete(`/admin/events/${eventId}/activities/${activityId}/slots/${slot.id}`);
            setActivitySlots(previous => previous.filter(item => item.id !== slot.id));
            setSlotForm(current => current?.id === slot.id ? null : current);
            setSelectedSlotDetails(current => current?.id === slot.id ? null : current);
            setSlotBookings([]);
            setPanelDelete(null);
        } catch (error) {
            setPanelDeleteError(error?.message || 'Impossibile eliminare il panel. Riprova.');
        } finally {
            setDeletingPanel(false);
        }
    };

    const saveSlot = async () => {
        if (!eventId || !activity?.id || !slotForm || saveSlotDisabled || savingSlot) {
            return;
        }

        setSavingSlot(true);
        setPanelFormError('');
        try {
            const startsAt = normalizeSlotDateValue(slotForm.starts_at);
            const endsAt = normalizeSlotDateValue(slotForm.ends_at);
            const canConfigureReservation = activity?.room_access_type !== 'public';
            const isReservable = canConfigureReservation ? slotForm.is_reservable !== false : false;

            const body = {
                name: slotForm.name?.trim(),
                description: slotForm.description?.trim() || null,
                ac_lists: slotSelectedLists.map(list => parseInt(list.id)).filter((id) => !Number.isNaN(id)),
                guest_ids: Array.isArray(slotForm.guest_ids) ? slotForm.guest_ids : [],
                moderator_ids: Array.isArray(slotForm.moderator_ids) ? slotForm.moderator_ids : [],
                starts_at: startsAt,
                ends_at: endsAt,
                panel_type: slotForm.panel_type || 'panel',
                is_reservable: isReservable,
                capacity: isReservable ? parseInt(slotForm.capacity, 10) : null,
            };

            if (slotForm?.id) {
                await api.put(`/admin/events/${eventId}/activities/${activity.id}/slots/${slotForm.id}`, body);
            } else {
                await api.post(`/admin/events/${eventId}/activities/${activity.id}/slots`, body);
            }

            setSlotForm(null);
            setSlotSelectedLists([]);
            setSlotLists([]);
            await getActivitySlots(activity.id);
        } catch (error) {
            console.error(error);
            setPanelFormError(error?.message || "Impossibile salvare il panel. Riprova.");
        }
        setSavingSlot(false);
    }

    const saveGuest = async () => {
        if (!eventId || !guest) {
            return;
        }

        setSavingGuest(true);
        try {
            const defaultSortOrder = guest?.id ? (guest.sort_order ?? 0) : (guests.length + 1);
            const body = {
                name: guest.name?.trim(),
                surname: guest.surname?.trim() || null,
                role: guest.role?.trim() || null,
                company: guest.company?.trim() || null,
                image: guest.image?.trim() || null,
                bio: guest.bio?.trim() || null,
                linkedin_url: guest.linkedin_url?.trim() || null,
                website_url: guest.website_url?.trim() || null,
                course_ids: (Array.isArray(guest.course_ids) ? guest.course_ids : [])
                    .map((id) => parseInt(id, 10))
                    .filter((id) => !Number.isNaN(id)),
                notes: guest.notes?.trim() || null,
                sort_order: Number.isNaN(parseInt(defaultSortOrder, 10)) ? 0 : parseInt(defaultSortOrder, 10),
            };

            if (guest?.id) {
                await api.put(`/admin/events/${eventId}/guests/${guest.id}`, body);
            } else {
                await api.post(`/admin/events/${eventId}/guests`, body);
            }

            setGuest(null);
            await getGuests();
        } catch (error) {
            console.error(error);
        }
        setSavingGuest(false);
    }

    const saveStand = async () => {
        if (!eventId || !event || !stand) {
            return;
        }

        setSavingStand(true);
        try {
            const payload = {
                name: normalizeStandText(stand?.name),
                stand_number: stand.stand_number === '' || stand.stand_number == null ? null : Number(stand.stand_number),
                company_id: stand?.company_id || null,
                company_name: normalizeStandText(stand?.company_name),
                description: normalizeStandText(stand?.description),
                company_logo: normalizeStandText(stand?.company_logo) || null,
            };

            const savedStand = stand?.id
                ? await api.put(`/admin/events/${eventId}/stands/${stand.id}`, payload)
                : await api.post(`/admin/events/${eventId}/stands`, payload);

            setEvent((prev) => {
                if (!prev) {
                    return prev;
                }

                const currentStands = Array.isArray(prev?.stands) ? prev.stands : [];
                const nextStands = stand?.id
                    ? currentStands.map((item) => item.id === savedStand.id ? { ...item, ...savedStand } : item)
                    : [...currentStands, savedStand];

                return {
                    ...prev,
                    stands: nextStands,
                };
            });
            setStand(null);
        } catch (error) {
            console.error(error);
        }
        setSavingStand(false);
    }

    const deleteStand = async (standItem) => {
        if (!eventId || !event || !standItem?.id) {
            return;
        }

        const confirmed = window.confirm(`Confermi l'eliminazione dello stand ${standItem.name || ''}?`);
        if (!confirmed) {
            return;
        }

        setDeletingStandId(standItem.id);
        try {
            await api.delete(`/admin/events/${eventId}/stands/${standItem.id}`);

            setEvent((prev) => {
                if (!prev) {
                    return prev;
                }

                return {
                    ...prev,
                    stands: (Array.isArray(prev?.stands) ? prev.stands : []).filter((item) => item.id !== standItem.id),
                };
            });
            if (stand?.id === standItem.id) {
                setStand(null);
            }
        } catch (error) {
            console.error(error);
        }
        setDeletingStandId(null);
    }

    const openStandTalentQr = async (standItem) => {
        if (!eventId || !standItem?.id || !standItem?.company_id) {
            return;
        }

        setStandTalentQr({ stand: standItem, data: null });
        setLoadingStandTalentQr(true);
        try {
            const response = await api.get(`/admin/events/${eventId}/stands/${standItem.id}/talent-village-qrcode`);
            setStandTalentQr({ stand: standItem, data: response });
        } catch (error) {
            console.error(error);
            setStandTalentQr(null);
        }
        setLoadingStandTalentQr(false);
    }

    const downloadStandTalentQr = () => {
        const qrCode = standTalentQr?.data?.qr_code;
        if (!qrCode) return;
        const link = document.createElement('a');
        link.href = `data:image/png;base64,${qrCode}`;
        link.download = `talent-village-stand-${standTalentQr?.stand?.id || 'qr'}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }

    const deleteGuest = async (guestItem) => {
        if (!eventId || !guestItem?.id) {
            return;
        }

        const confirmed = window.confirm(`Confermi l'eliminazione dell'ospite ${guestItem.name || ''} ${guestItem.surname || ''}?`);
        if (!confirmed) {
            return;
        }

        setDeletingGuestId(guestItem.id);
        try {
            await api.delete(`/admin/events/${eventId}/guests/${guestItem.id}`);
            if (guest?.id === guestItem.id) {
                setGuest(null);
            }
            await getGuests();
        } catch (error) {
            console.error(error);
        }
        setDeletingGuestId(null);
    }

    const handleGuestsSort = useCallback((rows) => {
        // Keep original records: table rows contain display-only values.
        setGuestsOrderDraft(previous => {
            const byId = new Map(previous.map(item => [item.id, item]));
            return rows.map((row, index) => ({ ...byId.get(row.id), sort_order: index + 1 }));
        });
    }, []);

    const startGuestsOrdering = useCallback(() => {
        if (guests.length <= 1) {
            return;
        }

        setGuestsOrderDraft(guests.map((item, index) => ({
            ...item,
            sort_order: index + 1,
        })));
        setOrderingGuests(true);
    }, [guests]);

    const cancelGuestsOrdering = useCallback(() => {
        setOrderingGuests(false);
        setGuestsOrderDraft([]);
    }, []);

    const saveGuestsOrder = useCallback(async () => {
        if (!eventId || guestsOrderDraft.length === 0) {
            return;
        }

        setSavingGuestsOrder(true);
        try {
            await Promise.all(
                guestsOrderDraft.map((item, index) => {
                    const body = {
                        name: item.name?.trim(),
                        surname: item.surname?.trim() || null,
                        role: item.role?.trim() || null,
                        company: item.company?.trim() || null,
                        image: item.image || null,
                        bio: item.bio?.trim() || null,
                        linkedin_url: item.linkedin_url?.trim() || null,
                        website_url: item.website_url?.trim() || null,
                        course_ids: (Array.isArray(item.course_ids) ? item.course_ids : [])
                            .map((id) => parseInt(id, 10))
                            .filter((id) => !Number.isNaN(id)),
                        notes: item.notes?.trim() || null,
                        sort_order: index + 1,
                    };

                    return api.put(`/admin/events/${eventId}/guests/${item.id}`, body);
                })
            );

            await getGuests();
            setOrderingGuests(false);
            setGuestsOrderDraft([]);
        } catch (error) {
            console.error(error);
        }
        setSavingGuestsOrder(false);
    }, [eventId, getGuests, guestsOrderDraft]);

    return (
        <HeaderFooterLayout>
            <div className={styles.container}>
                <div className={styles.section}>
                    <div className={styles.sectionInner}>
                        <header className={styles.header}>
                            <Breadcrumb items={[{ label: 'Eventi', to: '/events' }, { label: event?.name || 'Caricamento…' }]} />
                        </header>
                        <div className={[styles.pageSection, styles.managementSection].join(' ')}>
                            <h2 className={styles.pageSectionTitle}>Gestione evento</h2>

                            <div className={styles.managementTopCards}>
                                <Card appearance="modern"
                                    title="Scheda evento"
                                    actions={<div className={styles.cardActions}>
                                        <Button appearance="subtle" onClick={() => setEventNotificationOpen(true)}>Invia notifica in-app</Button>
                                        <Button appearance="subtle" requiresEdit onClick={() => navigate(`/events/${eventId}/editor`)}>Apri nell’editor</Button>
                                    </div>}
                                >
                                    <div className={styles.cardInner}>
                                        <div className={styles.eventCardBody}>
                                            {loading &&
                                                <div className={styles.eventCardSkeleton}>
                                                    <Skeleton type="rect" width={50} height={50} borderRadius="12px" />
                                                    <div className={styles.eventCardSkeletonInfo}>
                                                        <Skeleton type="rect" width={150} height={20} borderRadius="4px" />
                                                        <Skeleton type="rect" width={300} height={15} borderRadius="4px" />
                                                    </div>
                                                    <Skeleton type="rect" width={60} height={30} borderRadius="8px" />
                                                </div>
                                            }
                                            {!loading && event &&
                                                <div className={styles.eventCardRow}>
                                                    <div className={styles.eventCardMain}>
                                                        <div className={styles.eventCardContent}>
                                                            <div className={styles.eventCardTitleRow}>
                                                                <img src={event.thumbnail} className={styles.eventCardThumbnail} alt="" />
                                                                <div className={styles.eventCardHeaderContent}>
                                                                    <h1 className={styles.eventCardName}>{event.name}</h1>
                                                                    <TruncatedText
                                                                        className={styles.eventCardDescription}
                                                                        text={event.description}
                                                                        maxLength={256}
                                                                    />
                                                                </div>
                                                            </div>
                                                            <div className={styles.eventMeta}>
                                                                <div className={styles.eventMetaItem}>
                                                                    <div className={styles.eventMetaLabel}>Inizio</div>
                                                                    <div className={styles.eventMetaValue}>
                                                                        {event?.starts_at
                                                                            ? `${formatDateV2(event.starts_at, { day: '2-digit', month: '2-digit', year: 'numeric' })} ${formatTimeV2(event.starts_at)}`
                                                                            : '—'}
                                                                    </div>
                                                                </div>
                                                                <div className={styles.eventMetaItem}>
                                                                    <div className={styles.eventMetaLabel}>Fine</div>
                                                                    <div className={styles.eventMetaValue}>
                                                                        {event?.ends_at
                                                                            ? `${formatDateV2(event.ends_at, { day: '2-digit', month: '2-digit', year: 'numeric' })} ${formatTimeV2(event.ends_at)}`
                                                                            : '—'}
                                                                    </div>
                                                                </div>
                                                                <div className={styles.eventMetaItem}>
                                                                    <div className={styles.eventMetaLabel}>Biglietti Venduti</div>
                                                                    <div className={styles.eventMetaValue}>{formatNumber(total)}</div>
                                                                </div>
                                                            </div>
                                                            {locationDetails.length > 0 && (
                                                                <div className={styles.eventLocationSection}>
                                                                    <div className={styles.eventLocationTitle}>Location</div>
                                                                    <div className={styles.eventLocationGrid}>
                                                                        {locationDetails.map((item) => (
                                                                            <div key={item.key} className={styles.eventMetaItem}>
                                                                                <div className={styles.eventMetaLabel}>{item.label}</div>
                                                                                <div className={styles.eventMetaValue}>{item.value}</div>
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div className={styles.eventActions}>
                                                        <StatusBadge appearance="soft" label={t('status.' + event.status)} color={getStatusColor(event.status)} />
                                                    </div>
                                                </div>
                                            }
                                        </div>
                                    </div>
                                </Card>

                                <Card appearance="modern" title="Scanner">
                                    <button type="button" className={styles.scanTicketsCardInner} onClick={openQrScanner}>
                                        <QrCodeIcon className={styles.scanTicketsIcon} />
                                        <span className={styles.scanTicketsLabel}>Scansiona biglietti</span>
                                    </button>
                                </Card>
                            </div>

                            <Card appearance="modern" title="TIPOLOGIE DI BIGLIETTO" integratedTable actions={<CreateButton onClick={() => setTicketType({ name: '', accessLevel: accessLevels[0].value })}>
                                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>

                                        Aggiungi
                                    </span>
                                </CreateButton>}>

                                <Table appearance="minimal"
                                    loading={loadingTypes}
                                    loadingN={3}
                                    onRowClick={async (element) => {
                                        setTicketType({
                                            ...element,
                                            price: parseFloat(element.product?.price / 100) || '',
                                        });
                                    }}
                                    style={{ borderRadius: 0 }}
                                    columns={[
                                        { key: 'id', label: 'ID', type: 'number' },
                                        { key: 'name', label: 'Nome', type: 'string' },
                                        { key: 'price', label: 'Prezzo (€)', type: 'custom' },
                                        { key: 'max_quantity', label: 'Quantità Massima', type: 'number' },
                                        { key: 'access_level', label: 'Livello di Accesso', type: 'string' }
                                    ]}
                                    elements={ticketTypes.map(type => ({
                                        ...type,
                                        price: {
                                            value: type.product?.price, type: 'number', component: <span>{type.product ? formatPrice(type.product?.price / 100) : 'FREE'}</span>,
                                        },
                                        maxQuantity: type.max_quantity,
                                        accessLevel: type.access_level
                                    }))}
                                />
                            </Card>

                            <Card appearance="modern" title="STANZE" integratedTable actions={<div className={styles.cardActions}>
                                    {!orderingActivities &&
                                        <Button
                                            appearance="subtle"
                                            disabled={savingActivitiesOrder || activities.length <= 1}
                                            onClick={startActivitiesOrdering}
                                        >
                                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
                                                <ReorderIcon style={{ width: '11px', height: '11px' }} />
                                                Ordina
                                            </span>
                                        </Button>
                                    }
                                    {orderingActivities &&
                                        <>
                                            <Button
                                                appearance="subtle"
                                                disabled={savingActivitiesOrder || activitiesOrderDraft.length <= 1}
                                                onClick={saveActivitiesOrder}
                                            >
                                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
                                                    <CheckIcon style={{ width: '11px', height: '11px' }} />
                                                    {savingActivitiesOrder ? "Salvataggio..." : "Salva"}
                                                </span>
                                            </Button>
                                            <Button
                                                appearance="subtle"
                                                disabled={savingActivitiesOrder}
                                                onClick={cancelActivitiesOrdering}
                                            >
                                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
                                                    <CloseIcon style={{ width: '11px', height: '11px' }} />
                                                    Annulla
                                                </span>
                                            </Button>
                                        </>
                                    }
                                    {!orderingActivities &&
                                        <CreateButton onClick={() => {
                                                setSlotForm(null);
                                                setSlotSelectedLists([]);
                                                setSlotLists([]);
                                                setSelectedSlotDetails(null);
                                                setSlotRoomQr(null);
                                                setSlotBookings([]);
                                                setActivity({
                                                    name: '',
                                                    description: '',
                                                    room_access_type: 'mixed',
                                                    slot_capacity: 60,
                                                    max_slots_per_user: 1,
                                                    sort_order: activities.length + 1,
                                                });
                                            }}>
                                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>

                                                Aggiungi
                                            </span>
                                        </CreateButton>
                                    }
                                </div>}>

                                {orderingActivities && <p className={styles.orderingHint}>Trascina gli handle per cambiare l’ordine, poi premi Salva. Puoi anche usare le frecce su e giù sull’handle.</p>}
                                <Table appearance="minimal"
                                        ordering={orderingActivities}
                                        orderingHandles
                                        orderingDisabled={savingActivitiesOrder}
                                        onOrdering={handleActivitiesSort}
                                    loading={loadingActivities}
                                    loadingN={5}
                                    onRowClick={(item) => {
                                        const normalizedRoomAccessType =
                                            item.room_access_type === 'public' || item.room_access_type === 'Pubblica'
                                                ? 'public'
                                                : item.room_access_type === 'reservable_only' || item.room_access_type === 'Solo prenotabile'
                                                    ? 'reservable_only'
                                                    : 'mixed';

                                        setActivity({
                                            id: item.id,
                                            name: item.name || '',
                                            stand_number: item.stand_number ?? '',
                                            description: item.description || '',
                                            room_access_type: normalizedRoomAccessType,
                                            slot_capacity: item.slot_capacity,
                                            max_slots_per_user: item.max_slots_per_user,
                                            sort_order: item.sort_order ?? 0,
                                        });
                                        setSlotForm(null);
                                        setSlotSelectedLists([]);
                                        setSlotLists([]);
                                        setSelectedSlotDetails(null);
                                        setSlotRoomQr(null);
                                        setSlotBookings([]);
                                    }}
                                    style={{ borderRadius: 0, maxHeight: '400px', overflowY: 'auto' }}
                                    columns={[
                                        { key: 'id', label: 'ID', type: 'number' },
                                        { key: 'sort_order_display', label: 'ORDINE', type: 'custom' },
                                        { key: 'name', label: 'NOME', type: 'string' },
                                        { key: 'description', label: 'DESCRIZIONE', type: 'string' },
                                        { key: 'room_access_type_display', label: 'ACCESSO STANZA', type: 'custom' },
                                        { key: 'slot_capacity_display', label: 'POSTI PANEL', type: 'custom' },
                                        { key: 'max_slots_per_user_display', label: 'MAX PANEL PRENOTABILI / UTENTE', type: 'custom' },
                                        { key: 'created_at', label: 'CREATA IL', type: 'datetime' },
                                    ]}
                                    elements={orderedActivities.map((item, index) => ({
                                        ...item,
                                        sort_order_display: {
                                            value: index + 1,
                                            type: 'number',
                                            component: <span>{index + 1}</span>
                                        },
                                        description: item.description || '—',
                                        room_access_type_display: {
                                            value: item.room_access_type,
                                            type: 'string',
                                            component: (
                                                <span>
                                                    {item.room_access_type === 'public'
                                                        ? 'Pubblica'
                                                        : item.room_access_type === 'reservable_only'
                                                            ? 'Solo prenotabile'
                                                            : 'Mista'}
                                                </span>
                                            )
                                        },
                                        slot_capacity_display: item.room_access_type === 'public'
                                            ? { value: 0, type: 'string', component: <span>--</span> }
                                            : { value: item.slot_capacity, type: 'number', component: <span>{item.slot_capacity ?? '—'}</span> },
                                        max_slots_per_user_display: item.room_access_type === 'public'
                                            ? { value: 0, type: 'string', component: <span>--</span> }
                                            : { value: item.max_slots_per_user, type: 'number', component: <span>{item.max_slots_per_user ?? '—'}</span> },
                                        created_at: item.created_at,
                                    }))}
                                    defaultSort={{ key: 'sort_order_display', direction: 'asc' }}
                                />
                            </Card>

                            <Card appearance="modern" title="OSPITI" integratedTable actions={<div className={styles.cardActions}>
                                    {!orderingGuests &&
                                        <Button
                                            appearance="subtle"
                                            disabled={savingGuestsOrder || guests.length <= 1}
                                            onClick={startGuestsOrdering}
                                        >
                                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
                                                <ReorderIcon style={{ width: '11px', height: '11px' }} />
                                                Ordina
                                            </span>
                                        </Button>
                                    }
                                    {orderingGuests &&
                                        <>
                                            <Button
                                                appearance="subtle"
                                                disabled={savingGuestsOrder || guestsOrderDraft.length <= 1}
                                                onClick={saveGuestsOrder}
                                            >
                                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
                                                    <CheckIcon style={{ width: '11px', height: '11px' }} />
                                                    {savingGuestsOrder ? "Salvataggio..." : "Salva"}
                                                </span>
                                            </Button>
                                            <Button
                                                appearance="subtle"
                                                disabled={savingGuestsOrder}
                                                onClick={cancelGuestsOrdering}
                                            >
                                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
                                                    <CloseIcon style={{ width: '11px', height: '11px' }} />
                                                    Annulla
                                                </span>
                                            </Button>
                                        </>
                                    }
                                    {!orderingGuests &&
                                        <CreateButton onClick={() => {
                                                setGuest({
                                                    name: '',
                                                    surname: '',
                                                    role: '',
                                                    company: '',
                                                    image: null,
                                                    bio: '',
                                                    linkedin_url: '',
                                                    website_url: '',
                                                    course_ids: [],
                                                    notes: '',
                                                    sort_order: guests.length + 1,
                                                });
                                            }}>
                                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>

                                                Aggiungi
                                            </span>
                                        </CreateButton>
                                    }
                                </div>}>

                                {orderingGuests && <p className={styles.orderingHint}>Trascina gli handle per cambiare l’ordine, poi premi Salva. Puoi anche usare le frecce su e giù sull’handle.</p>}
                                {!orderingGuests &&
                                    <div className={styles.search} style={{ marginTop: '0.75rem' }}>
                                        <TextInput appearance="modern"
                                            type="search"
                                            placeholder="Cerca ospite per nome, ruolo o azienda"
                                            value={guestQ}
                                            style={{ width: '100%' }}
                                            onKeyUp={(value) => {
                                                setGuestQ(value);
                                            }}
                                        />
                                    </div>
                                }
                                <Table appearance="minimal"
                                        ordering={orderingGuests}
                                        orderingHandles
                                        orderingDisabled={savingGuestsOrder}
                                        onOrdering={handleGuestsSort}
                                        loading={loadingGuests}
                                        loadingN={4}
                                        onRowClick={(row) => {
                                            const item = guests.find((guest) => guest.id === row.id);
                                            if (!item) return;
                                            setGuest({
                                                id: item.id,
                                                name: item.name || '',
                                            stand_number: item.stand_number ?? '',
                                                surname: item.surname || '',
                                                role: item.role || '',
                                                company: item.company || '',
                                                image: item.image || null,
                                                bio: item.bio || '',
                                                linkedin_url: item.linkedin_url || '',
                                                website_url: item.website_url || '',
                                                course_ids: Array.isArray(item.course_ids) ? item.course_ids : [],
                                                notes: item.notes || '',
                                                sort_order: item.sort_order ?? 0,
                                            });
                                        }}
                                        style={{ borderRadius: 0, maxHeight: '400px' }}
                                        columns={[
                                            { key: 'id', label: 'ID', type: 'number' },
                                            { key: 'full_name', label: 'NOME', type: 'string' },
                                            { key: 'role', label: 'RUOLO', type: 'string' },
                                            { key: 'company', label: 'AZIENDA', type: 'string' },
                                            { key: 'sort_order_display', label: 'ORDINE', type: 'custom' },
                                        ]}
                                        elements={(orderingGuests ? orderedGuests : filteredGuests).map((item, index) => ({
                                            ...item,
                                            full_name: `${item.name || ''} ${item.surname || ''}`.trim() || '—',
                                            role: item.role || '—',
                                            company: item.company || '—',
                                            sort_order_display: {
                                                value: index + 1,
                                                type: 'number',
                                                component: <span>{index + 1}</span>
                                            },

                                        }))}
                                        defaultSort={{ key: 'sort_order_display', direction: 'asc' }}
                                    />
                            </Card>

                            {event && (
                                <EventSponsors key={event.id} event={event}
                                    onSaved={(sponsors) => setEvent((prev) => ({ ...prev, sponsors }))} />
                            )}

                            <Card appearance="modern" title="STAND EVENTO" integratedTable actions={<div className={styles.cardActions}>{standsOrderDraft ? <>
                                    <Button appearance="subtle" disabled={savingStandsOrder} onClick={saveStandsOrder}>{savingStandsOrder ? "Salvataggio..." : "Salva"}</Button>
                                    <Button appearance="subtle" disabled={savingStandsOrder} onClick={() => { setStandsOrderDraft(null); setStandsOrderError(''); }}>Annulla</Button>
                                </> : <><Button appearance="subtle" disabled={(event?.stands?.length || 0) < 2} onClick={() => { setStandsOrderError(''); setStandsOrderDraft([...event.stands]); }}>Ordina</Button><CreateButton onClick={() => {
                                        setStand({
                                            name: '',
                                            stand_number: '',
                                            company_name: '',
                                            description: '',
                                            company_logo: '',
                                            company_id: null,
                                        });
                                    }}>
                                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>

                                        Aggiungi
                                    </span>
                                </CreateButton></>}</div>}>
                                {standsOrderError && <p role="alert">{standsOrderError}</p>}
                                {standsOrderDraft && <p className={styles.orderingHint}>Trascina gli handle o usa le frecce su e giù, poi premi Salva.</p>}

                                <Table appearance="minimal"
                                    ordering={Boolean(standsOrderDraft)}
                                    orderingHandles
                                    orderingDisabled={savingStandsOrder}
                                    onOrdering={rows => setStandsOrderDraft(previous => rows.map(row => previous.find(item => item.id === row.id)))}
                                    defaultSort={{ key: 'sort_order', direction: 'asc' }}
                                    loading={loading}
                                    loadingN={3}
                                    onRowClick={(item) => {
                                        setStand({
                                            id: item.id,
                                            name: item.name || '',
                                            stand_number: item.stand_number ?? '',
                                            company_name: item.company_name || '',
                                            description: typeof item.description === 'string' ? item.description : item?.description?.value || '',
                                            company_logo: typeof item.company_logo === 'string' ? item.company_logo : item?.company_logo?.value || '',
                                            company_id: item.company_id || null,
                                        });
                                    }}
                                    style={{ borderRadius: 0 }}
                                    columns={[
                                        { key: 'sort_order', label: 'ORDINE', type: 'number' },
                                        { key: 'id', label: 'ID', type: 'number' },
                                        { key: 'stand_number', label: 'NUMERO STAND', type: 'number' },
                                        { key: 'name', label: 'NOME STAND', type: 'string' },
                                        { key: 'company_name', label: 'AZIENDA', type: 'string' },
                                        { key: 'description', label: 'DESCRIZIONE', type: 'custom' },
                                        { key: 'company_logo', label: 'LOGO', type: 'custom', handleClick: true },
                                        { key: 'talent_qr', label: 'QR TALENT VILLAGE', type: 'custom', handleClick: true },
                                        { key: 'actions', label: 'AZIONI', type: 'custom', handleClick: true },
                                    ]}
                                    elements={(standsOrderDraft || (Array.isArray(event?.stands) ? event.stands : [])).map((item, index) => ({
                                        ...item,
                                        sort_order: index + 1,
                                        description: {
                                            value: item.description || '',
                                            type: 'string',
                                            component: <div className={styles.standDescriptionClamp}>{item.description || '—'}</div>
                                        },
                                        company_logo: {
                                            value: item.company_logo || '',
                                            type: 'string',
                                            component: item.company_logo
                                                ? <img src={item.company_logo} alt={item.company_name || item.name || ''} className={styles.standLogoThumb} />
                                                : <span>—</span>
                                        },
                                        talent_qr: {
                                            value: item.company_id || '',
                                            type: 'string',
                                            component: item.company_id ? (
                                                <Button appearance='subtle' onClick={() => openStandTalentQr(item)}>
                                                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
                                                        <QrCodeIcon style={{ width: '13px', height: '13px' }} />
                                                        Mostra QR
                                                    </span>
                                                </Button>
                                            ) : <span>Associa un'azienda</span>
                                        },
                                        actions: {
                                            value: item.id,
                                            type: 'number',
                                            component: (
                                                <Button
                                                    appearance='subtle'
                                                    disabled={Boolean(standsOrderDraft) || deletingStandId === item.id}
                                                    onClick={() => deleteStand(item)}
                                                >
                                                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
                                                        <DeleteIcon style={{ width: '11px', height: '11px' }} />
                                                        {deletingStandId === item.id ? "Eliminazione..." : "Elimina"}
                                                    </span>
                                                </Button>
                                            )
                                        },
                                    }))}
                                />
                            </Card>

                            <EventTalentCvs key={eventId} eventId={eventId} />

                            <Card appearance="modern" title="BIGLIETTI" integratedTable actions={<div className={styles.ticketsActions}>
                                    <Button
                                        appearance='subtle'
                                        onClick={openQrScanner}
                                    >
                                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
                                            <QrCodeIcon style={{ width: '11px', height: '11px' }} />
                                            Scansiona QR
                                        </span>
                                    </Button>
                                    <Button appearance='subtle' disabled={exporting} onClick={() => exportTickets()}>
                                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
                                            <DownloadIcon style={{ width: '11px', height: '11px' }} />
                                            {exporting ? "Esportando..." : "Esporta CSV"}
                                        </span>
                                    </Button>
                                    <Button appearance='subtle' disabled={exportingList} onClick={() => exportTicketsList()}>
                                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
                                            <DownloadIcon style={{ width: '11px', height: '11px' }} />
                                            {exportingList ? "Esportando..." : "Esporta lista"}
                                        </span>
                                    </Button>
                                </div>}>
                                <div className={styles.search}>
                                    <TextInput appearance="modern" type="search" placeholder="Cerca per nome o email" style={{ width: '100%' }} onKeyUp={(value) => {
                                        setQ(value);
                                    }} />
                                </div>

                                <Table appearance="minimal"
                                    loading={loadingTickets}
                                    loadingN={25}
                                    onRowClick={(ticket) => {
                                        setManualScanError('');
                                        setManualScanSuccess('');
                                        setSelectedTicket(ticket);
                                    }}
                                    style={{ borderRadius: 0 }}
                                    columns={[
                                        { key: 'id', label: 'ID', type: 'number' },
                                        { key: 'user_name', label: 'NOME', type: 'string' },
                                        { key: 'user_email', label: 'EMAIL', type: 'string' },
                                        { key: 'ticket_type_name', label: 'TIPOLOGIA BIGLIETTO', type: 'string' },
                                        { key: 'activities_count', label: 'PANEL PRENOTATI', type: 'custom' },
                                        { key: 'status', label: 'STATO', type: 'custom' },
                                        { key: 'created_at', label: 'DATA ACQUISTO', type: 'datetime' },
                                        { key: 'last_scan', label: 'ULTIMA SCANSIONE', type: 'custom' },
                                    ]}
                                    elements={tickets.map(ticket => ({
                                        ...ticket,
                                        user_name: ticket.user?.name + ' ' + ticket.user?.surname || '—',
                                        user_email: ticket.user?.email || '—',
                                        ticket_type_name: ticket.type?.name || '—',
                                        activities_count: (() => {
                                            const count = Array.isArray(ticket?.activity_bookings) ? ticket.activity_bookings.length : 0;
                                            return {
                                                value: count,
                                                type: 'number',
                                                component: <span>{count}</span>
                                            };
                                        })(),
                                        status: { value: ticket.status, component: getTicketStatusBadge(ticket.status), type: 'string' },
                                        last_scan: (() => {
                                            const latestScan = getLatestScan(ticket);
                                            if (!latestScan) {
                                                return { value: 0, type: 'string', component: <span>—</span> };
                                            }

                                            const dateValue = latestScan.scanned_at
                                                ? `${formatDateV2(latestScan.scanned_at, { day: '2-digit', month: '2-digit', year: 'numeric' })} ${formatTimeV2(latestScan.scanned_at)}`
                                                : '—';
                                            const adminName = latestScan.admin_name || '—';

                                            return {
                                                value: latestScan.scanned_at || '',
                                                type: 'string',
                                                component: (
                                                    <div className={styles.lastScanCell}>
                                                        <div className={styles.lastScanDate}>{dateValue}</div>
                                                        <div className={styles.lastScanAdmin}>{adminName}</div>
                                                    </div>
                                                )
                                            };
                                        })(),
                                        created_at: ticket.created_at
                                    }))}
                                    defaultSort={{ key: 'created_at', direction: 'desc' }}
                                />
                                {total > tickets.length &&
                                    <Pagination group={width > 540 ? 5 : 0} total={total} per={per} page={page} onChange={p => setPage(p)} loading={loadingTickets} />
                                }
                            </Card>
                        </div>
                        <div className={[styles.pageSection, styles.analyticsSection].join(' ')}>
                            <h2 className={styles.pageSectionTitle}>Analytics</h2>

                            <div className={styles.filters}>
                                <EasePicker
                                    options={analyticsDatePickerOptions}
                                    placeholder="Seleziona Date"
                                    startDate={analyticsFilters.from_date ? new Date(analyticsFilters.from_date) : undefined}
                                    endDate={analyticsFilters.to_date ? new Date(analyticsFilters.to_date) : undefined}
                                    className={styles.datePicker}
                                />
                            </div>

                            <div className={styles.analyticsGrid}>
                                {eventAnalyticsMetrics.map((metric) => {
                                    const difference = calculatePercentageDifference(metric.actual, metric.previous);
                                    return (
                                        <Card appearance="modern" key={metric.key} title={metric.title}>
                                            <MetricStatBlock
                                                title=""
                                                actualValue={formatNumber(metric.actual)}
                                                previousValue={formatNumber(metric.previous)}
                                                percentageValue={difference}
                                                isLoss={difference < 0}
                                                loading={loadingAnalytics}
                                                containerClassName={styles.analytic}
                                                titleClassName={styles.analyticTitle}
                                                actualClassName={styles.actualAnalytic}
                                                previousClassName={styles.previousAnalytic}
                                                skeletonClassName={styles.analyticSkeleton}
                                            />
                                        </Card>
                                    );
                                })}
                            </div>

                            {showLiveEntriesAnalytics &&
                                <>
                                    <div className={styles.liveEntriesStatsGrid}>
                                        <Card appearance="modern" title="BIGLIETTI SCANSIONATI">
                                            <MetricStatBlock
                                                title=""
                                                actualValue={formatNumber(liveEntriesStats.scannedTickets)}
                                                loading={loadingEventLiveEntriesAnalytics}
                                                containerClassName={styles.analytic}
                                                titleClassName={styles.analyticTitle}
                                                actualClassName={styles.actualAnalytic}
                                                previousClassName={styles.previousAnalytic}
                                                skeletonClassName={styles.analyticSkeleton}
                                            />
                                        </Card>

                                        <Card appearance="modern" title="TASSO DI AFFLUENZA">
                                            <MetricStatBlock
                                                title=""
                                                actualValue={formatPercentage(liveEntriesStats.attendanceRate)}
                                                loading={loadingEventLiveEntriesAnalytics}
                                                containerClassName={styles.analytic}
                                                titleClassName={styles.analyticTitle}
                                                actualClassName={styles.actualAnalytic}
                                                previousClassName={styles.previousAnalytic}
                                                skeletonClassName={styles.analyticSkeleton}
                                            />
                                        </Card>
                                    </div>

                                    <Card appearance="modern" title="ANDAMENTO INGRESSI PER ORA" integratedTable>
                                        <div className={styles.liveEntriesChartContainer}>
                                            <ResponsiveContainer width="100%" height={320}>
                                                <BarChart data={liveEntriesChartData} margin={{ top: 10, right: 16, left: 0, bottom: 0 }}>
                                                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(var(--text-color-rgb), 12%)" />
                                                    <XAxis dataKey="label" tick={{ fontSize: 12, fill: 'rgba(var(--text-color-rgb), 70%)' }} />
                                                    <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: 'rgba(var(--text-color-rgb), 70%)' }} />
                                                    <RechartsTooltip
                                                        cursor={{ fill: 'rgba(var(--text-color-rgb), 6%)' }}
                                                        formatter={(value) => [formatNumber(value), 'Biglietti scansionati']}
                                                        labelFormatter={(label) => formatHourRangeLabel(label)}
                                                    />
                                                    <Bar dataKey="scanned_tickets" fill="var(--primary)" radius={[6, 6, 0, 0]} />
                                                </BarChart>
                                            </ResponsiveContainer>
                                        </div>
                                    </Card>
                                </>
                            }

                            <div className={styles.geoUtmGrid}>
                                <Card appearance="modern" title="DISTRIBUZIONE GEOGRAFICA" integratedTable>
                                    <div className={styles.geoSelectorBar}>
                                        <Select portal appearance="modern"
                                            options={geoDimensionOptions}
                                            selected={geoDimensionOptions.find((option) => option.value === geoDimension) || geoDimensionOptions[0]}
                                            onSelect={(option) => setGeoDimension(option.value)}
                                        />
                                    </div>
                                    <Table appearance="minimal"
                                        loading={loadingAnalytics}
                                        loadingN={8}
                                        style={{ borderRadius: 0, maxHeight: '400px' }}
                                        columns={[
                                            { key: 'rank', label: '#', type: 'number' },
                                            { key: 'value', label: geoDimension === 'city' ? 'CITTÀ' : geoDimension === 'province' ? 'PROVINCIA' : 'REGIONE', type: 'string' },
                                            { key: 'count', label: 'NUMERO', type: 'custom' },
                                            { key: 'percentage', label: 'PERCENTUALE', type: 'custom' },
                                        ]}
                                        elements={geoDistributionRows.map((row) => ({
                                            ...row,
                                            count: {
                                                value: row.count,
                                                type: 'number',
                                                component: <span>{formatNumber(row.count)}</span>,
                                            },
                                            percentage: {
                                                value: row.percentage,
                                                type: 'number',
                                                component: <span>{row.percentage.toFixed(2)}%</span>,
                                            },
                                        }))}
                                        defaultSort={{ key: 'count', direction: 'desc' }}
                                    />
                                </Card>

                                <Card appearance="modern" title="UTM" integratedTable>
                                    <div className={styles.geoSelectorBar}>
                                        <Select portal appearance="modern"
                                            options={utmDimensionOptions}
                                            selected={utmDimensionOptions.find((option) => option.value === utmDimension) || utmDimensionOptions[0]}
                                            onSelect={(option) => setUtmDimension(option.value)}
                                        />
                                    </div>
                                    <Table appearance="minimal"
                                        loading={loadingAnalytics}
                                        loadingN={8}
                                        style={{ borderRadius: 0, maxHeight: '400px' }}
                                        columns={[
                                            { key: 'rank', label: '#', type: 'number' },
                                            { key: 'value', label: utmDimension.toUpperCase(), type: 'string' },
                                            { key: 'views', label: 'VIEWS', type: 'custom' },
                                            { key: 'tickets', label: 'BIGLIETTI', type: 'custom' },
                                            { key: 'conversion_rate', label: 'CONVERSION RATE', type: 'custom' },
                                        ]}
                                        elements={utmRows.map((row) => ({
                                            ...row,
                                            views: {
                                                value: row.views,
                                                type: 'number',
                                                component: <span>{formatNumber(row.views)}</span>,
                                            },
                                            tickets: {
                                                value: row.tickets,
                                                type: 'number',
                                                component: <span>{formatNumber(row.tickets)}</span>,
                                            },
                                            conversion_rate: {
                                                value: row.conversion_rate,
                                                type: 'number',
                                                component: <span>{row.conversion_rate.toFixed(2)}%</span>,
                                            },
                                        }))}
                                        defaultSort={{ key: 'views', direction: 'desc' }}
                                    />
                                </Card>
                            </div>
                        </div>

                        <div className={[styles.pageSection, styles.analyticsSection].join(' ')}>
                            <div className={styles.pageSectionTitle}>POST EVENTO</div>
                            <div className={styles.geoUtmGrid}>
                                <Card appearance="modern" title={`LEAD (${formatNumber(postTicketLeadRows.length)})`} integratedTable>
                                    <Table appearance="minimal"
                                        loading={loadingPostTicketActivity}
                                        loadingN={6}
                                        style={{ borderRadius: 0, maxHeight: '420px' }}
                                        columns={[
                                            { key: 'user', label: 'UTENTE', type: 'string' },
                                            { key: 'email', label: 'MAIL', type: 'string' },
                                            { key: 'course', label: 'CORSO', type: 'string', wrap: true },
                                            { key: 'status', label: 'STATUS', type: 'custom' },
                                            { key: 'application_created_at', label: 'CANDIDATURA', type: 'datetime' },
                                        ]}
                                        elements={postTicketLeadRows}
                                        defaultSort={{ key: 'application_created_at', direction: 'desc' }}
                                    />
                                </Card>

                                <Card appearance="modern" title={`ACQUISTI (${formatNumber(postTicketPurchaseRows.length)})`} integratedTable>
                                    <Table appearance="minimal"
                                        loading={loadingPostTicketActivity}
                                        loadingN={6}
                                        style={{ borderRadius: 0, maxHeight: '420px' }}
                                        columns={[
                                            { key: 'user', label: 'UTENTE', type: 'string' },
                                            { key: 'email', label: 'MAIL', type: 'string' },
                                            { key: 'order_no', label: 'ORDINE', type: 'string' },
                                            { key: 'product', label: 'PRODOTTO', type: 'string', wrap: true },
                                            { key: 'amount', label: 'PREZZO', type: 'custom' },
                                            { key: 'purchased_at', label: 'ACQUISTO', type: 'datetime' },
                                        ]}
                                        elements={postTicketPurchaseRows}
                                        defaultSort={{ key: 'purchased_at', direction: 'desc' }}
                                    />
                                </Card>
                            </div>
                        </div>

                    </div>
                </div>
            </div>
            <Dialog
                appearance="modern"
                open={eventNotificationOpen}
                title="Invia notifica in-app"
                onClose={() => setEventNotificationOpen(false)}
                style={{ width: 'min(640px, 100%)' }}
            >
                <div className={styles.eventNotificationsCard}>
                    <div className={styles.eventNotificationsReach}>
                        <div>
                            <div className={styles.eventNotificationsReachLabel}>Persone raggiungibili</div>
                            <div className={styles.eventNotificationsReachValue}>
                                {loadingEventNotificationStats ? '...' : formatNumber(eventNotificationStats.reachable_users)}
                            </div>
                        </div>
                        <div>
                            <div className={styles.eventNotificationsReachLabel}>Token push attivi</div>
                            <div className={styles.eventNotificationsReachValue}>
                                {loadingEventNotificationStats ? '...' : formatNumber(eventNotificationStats.reachable_tokens)}
                            </div>
                        </div>
                    </div>

                    <div className={styles.eventNotificationsForm}>
                        <Select portal appearance="modern"
                            selected={eventNotificationCategoryOptions.find((option) => option.value === eventNotificationForm.category) || eventNotificationCategoryOptions[0]}
                            options={eventNotificationCategoryOptions}
                            onSelect={(option) => setEventNotificationForm((prev) => ({ ...prev, category: option.value }))}
                            style={{ width: '100%' }}
                        />
                        <TextInput appearance="modern"
                            type="text"
                            label="Titolo"
                            placeholder="Titolo notifica"
                            value={eventNotificationForm.title}
                            onKeyUp={(value) => setEventNotificationForm((prev) => ({ ...prev, title: value }))}
                            style={{ width: '100%' }}
                        />
                        <TextInput appearance="modern"
                            type="textarea"
                            label="Body"
                            placeholder="Testo della notifica"
                            value={eventNotificationForm.body}
                            onKeyUp={(value) => setEventNotificationForm((prev) => ({ ...prev, body: value }))}
                            style={{ width: '100%', minHeight: '108px' }}
                        />
                        <TextInput appearance="modern"
                            type="text"
                            label="URL opzionale"
                            placeholder="https://... oppure /programma"
                            value={eventNotificationForm.url}
                            onKeyUp={(value) => setEventNotificationForm((prev) => ({ ...prev, url: value }))}
                            style={{ width: '100%' }}
                        />
                    </div>

                    {eventNotificationResult &&
                        <div className={styles.eventNotificationsResult}>
                            Notifica inviata: {formatNumber(eventNotificationResult.sent)} push consegnate, {formatNumber(eventNotificationResult.failed)} fallite.
                        </div>
                    }
                    {eventNotificationError &&
                        <div className={styles.eventNotificationsError}>{eventNotificationError}</div>
                    }

                    <div className={styles.eventNotificationsActions}>
                        <Button
                            appearance="subtle"
                            disabled={loadingEventNotificationStats || sendingEventNotification}
                            onClick={getEventNotificationStats}
                        >
                            Aggiorna conteggio
                        </Button>
                        <Button
                            disabled={
                                sendingEventNotification ||
                                eventNotificationStats.reachable_users <= 0 ||
                                !eventNotificationForm.title.trim() ||
                                !eventNotificationForm.body.trim()
                            }
                            onClick={sendEventNotification}
                        >
                            {sendingEventNotification ? "Invio..." : "Invia notifica"}
                        </Button>
                    </div>
                </div>
            </Dialog>
            {qrScannerOpen &&
                <div className={styles.qrScannerOverlay}>
                    <div className={styles.qrScannerHeader}>
                        <div className={typo.subtitle}>Scanner QR Biglietti</div>
                        <div className={styles.qrScannerHeaderActions}>
                            {videoInputDevices.length > 1 &&
                                <select
                                    className={styles.qrCameraSelect}
                                    value={selectedCameraId}
                                    onChange={(event) => {
                                        setSelectedCameraId(event.target.value);
                                        setScannerError('');
                                        setScannerSuccess('');
                                        setScannerPaused(false);
                                    }}
                                    aria-label='Seleziona fotocamera'
                                >
                                    {videoInputDevices.map((device, index) => (
                                        <option key={device.deviceId || index} value={device.deviceId}>
                                            {device.label?.trim() || `Fotocamera ${index + 1}`}
                                        </option>
                                    ))}
                                </select>
                            }
                            {scannerPaused &&
                                <Button appearance='text' onClick={() => {
                                    clearQrAutoResumeTimeout();
                                    setScannerError('');
                                    setScannerSuccess('');
                                    setScannerPaused(false);
                                }}>
                                    Scansiona altro
                                </Button>
                            }
                            <Button appearance='text' onClick={() => {
                                clearQrAutoResumeTimeout();
                                setQrScannerOpen(false);
                                setScannerPaused(false);
                                setScannerError('');
                                setScannerSuccess('');
                            }} title='Chiudi scanner' aria-label='Chiudi scanner'>
                                <CloseIcon width={18} height={18} />
                            </Button>
                        </div>
                    </div>

                    <div className={styles.qrScannerContent}>
                        <div className={styles.qrScannerCameraPanel}>
                            <video ref={qrVideoRef} className={styles.qrScannerVideo} autoPlay muted playsInline />
                            {(startingQrScanner || scanningTicket) &&
                                <div className={styles.qrScannerStatus}>Scansione in corso...</div>
                            }
                            {scannerError &&
                                <div className={styles.qrScannerError}>{scannerError}</div>
                            }
                            {!scannerError && scannerSuccess &&
                                <div className={styles.qrScannerSuccess}>{scannerSuccess}</div>
                            }
                            {!scannerError && !startingQrScanner && !scanningTicket && !scannerPaused &&
                                <div className={styles.qrScannerHint}>Inquadra il QR code del biglietto</div>
                            }
                        </div>

                        <div className={styles.qrScannerResultPanel}>
                            <div className={typo.caption}>ULTIMA SCANSIONE</div>

                            {!scannedTicket &&
                                <div className={styles.qrScannerEmpty}>Nessun ticket scansionato</div>
                            }

                            {scannedTicket &&
                                <>
                                    <div className={styles.qrScannerTicketInfo}>
                                        <div><strong>Codice:</strong> {scannedTicketDetails?.qr_token || lastScannedCode || '—'}</div>
                                        <div><strong>Intestatario:</strong> {scannedTicketDetails?.user ? `${scannedTicketDetails.user.name || ''} ${scannedTicketDetails.user.surname || ''}`.trim() || '—' : '—'}</div>
                                        <div><strong>Tipo biglietto:</strong> {scannedTicketDetails?.type?.name || '—'}</div>
                                    </div>

                                    <div className={typo.caption}>ISCRIZIONI PANEL</div>
                                    <div className={styles.ticketActivitiesList}>
                                        {getTicketActivityBookings(scannedTicketDetails).length === 0 &&
                                            <div className={styles.qrScannerEmpty}>Nessuna iscrizione a panel</div>
                                        }

                                        {getTicketActivityBookings(scannedTicketDetails).map((booking, index) => (
                                            <div className={styles.ticketActivityItem} key={`scan-booking-${booking.id || `${booking?.slot?.id}-${booking?.activity?.id}-${index}`}`}>
                                                <div className={styles.ticketActivityTime}>{getActivityBookingTimeRange(booking)}</div>
                                                <div className={styles.ticketActivityMeta}>{getActivityBookingMeta(booking)}</div>
                                            </div>
                                        ))}
                                    </div>

                                    <div className={typo.caption}>SCANSIONI EFFETTUATE</div>
                                    <div className={styles.qrScansList}>
                                        {(Array.isArray(scannedTicket.scans) ? scannedTicket.scans : []).length === 0 &&
                                            <div className={styles.qrScannerEmpty}>Nessuna scansione registrata</div>
                                        }

                                        {(Array.isArray(scannedTicket.scans) ? scannedTicket.scans : []).map((scan, index) => (
                                            <div className={styles.qrScanItem} key={`${scan.admin_id}-${scan.scanned_at}-${index}`}>
                                                <div><strong>Admin:</strong> {scan.admin_name || '—'}</div>
                                                <div><strong>Scansionato il:</strong> {scan.scanned_at ? `${formatDateV2(scan.scanned_at, { day: '2-digit', month: '2-digit', year: 'numeric' })} ${formatTimeV2(scan.scanned_at)}` : '—'}</div>
                                            </div>
                                        ))}
                                    </div>
                                </>
                            }
                        </div>
                    </div>
                </div>
            }
            <Dialog
                overflow="scroll"
                open={!!selectedTicket}
                title={selectedTicketDetails ? `Biglietto #${selectedTicketDetails.id}` : 'Biglietto'}
                onClose={() => {
                    setSelectedTicket(null);
                    setManualScanError('');
                    setManualScanSuccess('');
                }}
                action={
                    <Button disabled={manualScanLoading || !selectedTicketDetails?.qr_token} onClick={markTicketAsScannedNow}>
                        {manualScanLoading ? "Scansione..." : "Scansiona"}
                    </Button>
                }
            >
                <div className={styles.ticketTypeDialog}>
                    {manualScanError &&
                        <div className={styles.scanFeedbackError}>{manualScanError}</div>
                    }
                    {!manualScanError && manualScanSuccess &&
                        <div className={styles.scanFeedbackSuccess}>{manualScanSuccess}</div>
                    }

                    <div className={styles.ticketDetailGrid}>
                        <div className={styles.ticketDetailCard}>
                            <div className={styles.ticketDetailLabel}>Codice</div>
                            <div className={styles.ticketDetailValue}>{selectedTicketDetails?.qr_token || '—'}</div>
                        </div>
                        <div className={styles.ticketDetailCard}>
                            <div className={styles.ticketDetailLabel}>Tipo biglietto</div>
                            <div className={styles.ticketDetailValue}>{selectedTicketDetails?.type?.name || '—'}</div>
                        </div>
                        <div className={styles.ticketDetailCard}>
                            <div className={styles.ticketDetailLabel}>Data acquisto</div>
                            <div className={styles.ticketDetailValue}>
                                {selectedTicketDetails?.created_at
                                    ? `${formatDateV2(selectedTicketDetails.created_at, { day: '2-digit', month: '2-digit', year: 'numeric' })} ${formatTimeV2(selectedTicketDetails.created_at)}`
                                    : '—'}
                            </div>
                        </div>
                        <div className={styles.ticketDetailCard}>
                            <div className={styles.ticketDetailLabel}>Stato</div>
                            <div className={styles.ticketDetailValue}>
                                {selectedTicketDetails?.status ? getTicketStatusBadge(selectedTicketDetails.status) : '—'}
                            </div>
                        </div>
                        <div className={`${styles.ticketDetailCard} ${styles.ticketDetailCardWide}`}>
                            <div className={styles.ticketDetailLabel}>Intestatario</div>
                            <div className={styles.ticketDetailValue}>{selectedTicketDetails?.user ? `${selectedTicketDetails.user.name || ''} ${selectedTicketDetails.user.surname || ''}`.trim() || '—' : '—'}</div>
                        </div>
                    </div>

                    <div className={typo.caption}>ISCRIZIONI PANEL</div>
                    <div className={styles.ticketScansSection}>
                        <div className={styles.ticketActivitiesList}>
                            {getTicketActivityBookings(selectedTicketDetails).length === 0 &&
                                <div className={styles.qrScannerEmpty}>Nessuna iscrizione a panel</div>
                            }

                            {getTicketActivityBookings(selectedTicketDetails).map((booking, index) => (
                                <div className={styles.ticketActivityItem} key={`detail-booking-${booking.id || `${booking?.slot?.id}-${booking?.activity?.id}-${index}`}`}>
                                    <div className={styles.ticketActivityTime}>{getActivityBookingTimeRange(booking)}</div>
                                    <div className={styles.ticketActivityMeta}>{getActivityBookingMeta(booking)}</div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className={typo.caption}>SCANSIONI EFFETTUATE</div>
                    <div className={styles.ticketScansSection}>
                        <div className={styles.qrScansList}>
                            {(Array.isArray(selectedTicketDetails?.scans) ? selectedTicketDetails.scans : []).length === 0 &&
                                <div className={styles.qrScannerEmpty}>Nessuna scansione registrata</div>
                            }

                            {(Array.isArray(selectedTicketDetails?.scans) ? selectedTicketDetails.scans : []).map((scan, index) => (
                                <div className={styles.qrScanItem} key={`${scan.admin_id}-${scan.scanned_at}-${index}`}>
                                    <div><strong>Admin:</strong> {scan.admin_name || '—'}</div>
                                    <div><strong>Scansionato il:</strong> {scan.scanned_at ? `${formatDateV2(scan.scanned_at, { day: '2-digit', month: '2-digit', year: 'numeric' })} ${formatTimeV2(scan.scanned_at)}` : '—'}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </Dialog>
            <AlertDialog
                open={manualScanConfirm.open}
                title={'Confermare scansione?'}
                text={'Confermi la scansione manuale del biglietto selezionato?'}
                actions={[
                    {
                        label: 'CONFERMA',
                        onClick: async () => {
                            setManualScanConfirm({ open: false, code: null });
                            await runManualTicketScan();
                        }
                    }
                ]}
                onClose={() => setManualScanConfirm({ open: false, code: null })}
            />
            <Dialog appearance="modern"
                style={{ width: 'min(1100px, 100%)' }}
                open={!!activity}
                title={activity?.id ? activity.name || 'Gestione stanza' : 'Nuova stanza'}
                onClose={() => {
                    if (savingActivity) return;
                    setActivity(null);
                    setSlotForm(null);
                    setSlotSelectedLists([]);
                    setSlotLists([]);
                    setSelectedSlotDetails(null);
                    setSlotRoomQr(null);
                    setSlotBookings([]);
                }}
                action={
                    <div className={styles.dialogFooter}><Button appearance="subtle" disabled={savingActivity} onClick={() => setActivity(null)}>Chiudi</Button><Button appearance="subtle" additionalClass={styles.primarySaveButton} disabled={saveActivityDisabled || savingActivity} onClick={saveActivity}>
                        <CheckIcon aria-hidden="true" />
                        {savingActivity ? "Salvataggio…" : activity?.id ? "Salva stanza" : "Crea stanza"}
                    </Button></div>
                }
            >
                <div className={styles.programDialog}>
                    <p className={styles.dialogIntro}>Configura la stanza, le modalità di accesso e il programma dei panel.</p>
                    {roomFormError && <p className={styles.formError} role="alert">{roomFormError}</p>}
                    <div className={styles.roomTabs} role="tablist" aria-label="Gestione stanza"
                        onKeyDown={event => {
                            if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
                            event.preventDefault();
                            const next = !activity?.id || event.key === 'Home' ? 'information' : event.key === 'End' ? 'program' : activityTab === 'information' ? 'program' : 'information';
                            setActivityTab(next);
                            event.currentTarget.querySelector(`[data-room-tab="${next}"]`)?.focus();
                        }}>
                        <button type="button" role="tab" id="room-information-tab" data-room-tab="information"
                            aria-selected={activityTab === 'information'} aria-controls="room-information-panel"
                            tabIndex={activityTab === 'information' ? 0 : -1} onClick={() => setActivityTab('information')}>Informazioni</button>
                        <button type="button" role="tab" id="room-program-tab" data-room-tab="program"
                            aria-selected={activityTab === 'program'} aria-controls="room-program-panel" disabled={!activity?.id}
                            tabIndex={activityTab === 'program' ? 0 : -1} onClick={() => setActivityTab('program')}>Programma</button>
                    </div>
                    {!activity?.id && <p className={styles.dialogIntro}>Salva la stanza per aggiungere panel al programma.</p>}
                    <div className={styles.roomTabPanel} role="tabpanel" id="room-information-panel" aria-labelledby="room-information-tab" hidden={activityTab !== 'information'}>
                    <h3 className={styles.formSectionTitle}>Informazioni stanza</h3>
                    <div className={styles.programFormRow}>
                        <TextInput appearance="modern"
                            type='text'
                            label="Nome stanza" placeholder="Es. Sala principale"
                            value={activity?.name}
                            onKeyUp={(value) => setActivity({ ...activity, name: value })}
                            style={{ width: '100%' }}
                        />
                    </div>
                    <div className={styles.programFormRow}>
                        <TextInput appearance="modern"
                            type='text'
                            label="Descrizione" placeholder="Descrivi la stanza"
                            value={activity?.description}
                            onKeyUp={(value) => setActivity({ ...activity, description: value })}
                            style={{ width: '100%' }}
                        />
                    </div>
                    {activity?.room_access_type !== 'public' &&
                        <div className={styles.programFormRow}>
                            <TextInput appearance="modern"
                                type='number'
                                label="Capacità panel prenotabili"
                                placeholder={'Capacità panel prenotabili'}
                                value={activity?.slot_capacity}
                                onKeyUp={(value) => setActivity({ ...activity, slot_capacity: value })}
                                style={{ width: '100%' }}
                            />
                            <TextInput appearance="modern"
                                type='number'
                                label="Max panel prenotabili per utente"
                                placeholder={'Max panel prenotabili per utente'}
                                value={activity?.max_slots_per_user}
                                onKeyUp={(value) => setActivity({ ...activity, max_slots_per_user: value })}
                                style={{ width: '100%' }}
                            />
                        </div>
                    }
                    <div className={styles.programFormRow}>
                        <div className={styles.programField} role="group" aria-label="Modalità di accesso"><span>Modalità di accesso</span><Select disabled={savingActivity} portal appearance="modern"
                            options={roomAccessTypeOptions}
                            selected={roomAccessTypeOptions.find((item) => item.value === (activity?.room_access_type || 'mixed')) || roomAccessTypeOptions[1]}
                            onSelect={(item) => setActivity({ ...activity, room_access_type: item.value })}
                        /></div>
                    </div>
                    {activity?.id && ['mixed', 'reservable_only'].includes(activity.room_access_type) && (
                        <div className={styles.slotRoomQrCard}>
                            <div className={styles.slotRoomQrInfo}>
                                <div className={styles.slotDetailsMetaLabel}>QR STANZA</div>
                                <div className={styles.slotRoomQrHint}>
                                    Questo QR appartiene alla stanza. L'app iscrive l'utente al panel disponibile in base alla finestra oraria e capienza.
                                </div>
                            </div>

                            {slotRoomQr?.code ? (
                                <img
                                    src={`data:image/png;base64,${slotRoomQr.code}`}
                                    alt="QR stanza"
                                    className={styles.slotRoomQrImage}
                                />
                            ) : (
                                <div className={styles.slotBookingQrMissing}>QR non disponibile</div>
                            )}

                            <button
                                type='button'
                                className={styles.slotBookingQrDownloadBtn}
                                onClick={downloadRoomQrPng}
                                disabled={!slotRoomQr?.code}
                            >
                                Scarica PNG
                            </button>
                        </div>
                    )}
                    </div>
                    <div className={styles.roomTabPanel} role="tabpanel" id="room-program-panel" aria-labelledby="room-program-tab" hidden={activityTab !== 'program'}>
                    {activity?.id && activity?.room_access_type !== 'public' && (
                        <div className={styles.programFormRow} style={{ justifyContent: 'flex-end' }}>
                            <Button
                                appearance='subtle'
                                disabled={exportingActivityId === activity.id}
                                onClick={() => exportActivityParticipants(activity)}
                            >
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
                                    <DownloadIcon style={{ width: '11px', height: '11px' }} />
                                    {exportingActivityId === activity.id ? "Esportando..." : "Esporta CSV"}
                                </span>
                            </Button>
                        </div>
                    )}
                    {activity?.id && (
                        <>
                            <Card appearance="modern" integratedTable title="Programma della stanza" actions={
                            <CreateButton onClick={() => {
                                    const eventStartDate = parseEventDate(event?.starts_at);
                                    const eventEndDate = parseEventDate(event?.ends_at);

                                    const safeStartDate = eventStartDate && !Number.isNaN(eventStartDate.getTime()) ? eventStartDate : null;
                                    const safeEndDate = eventEndDate && !Number.isNaN(eventEndDate.getTime()) ? eventEndDate : safeStartDate;

                                    setPanelFormError('');
                                    setSlotForm({
                                        name: '',
                                        description: '',
                                        ac_lists: [],
                                        guest_ids: [],
                                        moderator_ids: [],
                                        panel_type: 'panel',
                                        is_reservable: activity?.room_access_type === 'public' ? false : true,
                                        starts_at: safeStartDate?.toISOString().split('.')[0] || null,
                                        ends_at: safeEndDate?.toISOString().split('.')[0] || null,
                                        capacity: activity.slot_capacity || 60,
                                    });
                                    setSlotSelectedLists([]);
                                    setSlotLists([]);
                                }}>
                                Aggiungi panel
                            </CreateButton>}>
                            <p className={styles.programTableSummary}>{activitySlots.length} panel · Apri una riga per vedere i dettagli e gli iscritti.</p>
                            {!loadingActivitySlots && activitySlots.length === 0 && <p className={styles.slotBookingEmpty}>Nessun panel in programma. Aggiungi il primo panel della stanza.</p>}
                            <Table appearance="minimal"
                                loading={loadingActivitySlots}
                                loadingN={4}
                                onRowClick={(slotElement) => {
                                    openSlotDetailsDialog(slotElement?._raw || slotElement);
                                }}
                                style={{ border: 0, borderRadius: 0, boxShadow: 'none', marginTop: 0, maxHeight: '360px', overflowY: 'auto' }}
                                columns={[
                                    { key: 'id', label: 'ID', type: 'number' },
                                    { key: 'name', label: 'NOME', type: 'custom', wrap: true },
                                    { key: 'panel_type', label: 'TIPO', type: 'string' },
                                    { key: 'is_reservable', label: 'PRENOTABILE', type: 'string' },
                                    { key: 'starts_at', label: 'INIZIO', type: 'datetime' },
                                    { key: 'ends_at', label: 'FINE', type: 'datetime' },
                                    { key: 'capacity', label: 'CAPIENZA', type: 'number' },
                                    { key: 'booked_count', label: 'PRENOTATI', type: 'number' },
                                    { key: 'action', label: 'AZIONE', type: 'custom', handleClick: true },
                                ]}
                                elements={activitySlots.map((slot) => ({
                                    ...slot,
                                    _raw: slot,
                                    panel_type: slot.panel_type || 'panel',
                                    is_reservable: slot.is_reservable === false ? 'No' : 'Si',
                                    name: {
                                        value: slot.name || '',
                                        type: 'string',
                                        component: <div className={styles.slotNameClamp}>{slot.name || '—'}</div>
                                    },
                                    booked_count: slot.booked_count || 0,
                                    action: {
                                        value: slot.id,
                                        type: 'number',
                                        component: (
                                            <div className={styles.panelRowActions}>
                                                <Button appearance="subtle" additionalClass={styles.panelIconButton} requiresEdit
                                                    aria-label={`Modifica panel ${slot.name}`} title="Modifica panel" onClick={() => editPanel(slot)}><EditIcon aria-hidden="true" /></Button>
                                                <Button appearance="subtle" additionalClass={`${styles.panelIconButton} ${styles.panelDeleteButton}`} requiresEdit
                                                    aria-label={`Elimina panel ${slot.name}`} title="Elimina panel" onClick={() => requestPanelDelete(slot)}><DeleteIcon aria-hidden="true" /></Button>
                                            </div>
                                        )
                                    }
                                }))}
                                defaultSort={{ key: 'starts_at', direction: 'asc' }}
                            />
                            </Card>
                        </>
                    )}
                    </div>
                </div>
            </Dialog>
            <Dialog appearance="modern"
                style={{ width: 'min(880px, 100%)' }}
                open={!!slotForm}
                title={slotForm?.id ? 'Modifica panel' : 'Aggiungi panel'}
                onClose={() => {
                    if (savingSlot) return;
                    setSlotForm(null);
                    setSlotSelectedLists([]);
                    setSlotLists([]);
                }}
                action={
                    <div className={styles.dialogFooter}><div>{slotForm?.id && <Button appearance="subtle" additionalClass={styles.dangerAction} requiresEdit disabled={savingSlot} onClick={() => requestPanelDelete(slotForm)}>Elimina panel</Button>}</div><div className={styles.cardActions}><Button appearance="subtle" disabled={savingSlot} onClick={() => { setSlotForm(null); setSlotSelectedLists([]); setSlotLists([]); }}>Annulla</Button><Button appearance="subtle" additionalClass={styles.primarySaveButton} disabled={saveSlotDisabled || savingSlot} onClick={saveSlot}>
                        <CheckIcon aria-hidden="true" />
                        {savingSlot ? "Salvataggio..." : slotForm?.id ? "Salva panel" : "Crea panel"}
                    </Button></div></div>
                }
            >
                <div className={styles.programDialog}>
                    <p className={styles.dialogIntro}>Stanza: <strong>{activity?.name}</strong></p>
                    {panelFormError && <p className={styles.formError} role="alert">{panelFormError}</p>}
                    <h3 className={styles.formSectionTitle}>Contenuti del panel</h3>
                    <div className={styles.programFormRow}>
                        <TextInput appearance="modern"
                            type='text'
                            label="Titolo panel" placeholder="Titolo del panel"
                            value={slotForm?.name}
                            onKeyUp={(value) => setSlotForm({ ...slotForm, name: value })}
                            style={{ width: '100%' }}
                        />
                    </div>
                    <div className={styles.programFormRow}>
                        <div className={styles.programField} role="group" aria-label="Tipo di panel"><span>Tipo di panel</span><Select disabled={savingSlot} portal appearance="modern"
                            options={panelTypeOptions}
                            selected={panelTypeOptions.find((item) => item.value === (slotForm?.panel_type || 'panel')) || panelTypeOptions[0]}
                            onSelect={(item) => setSlotForm({ ...slotForm, panel_type: item.value })}
                        /></div>
                        {activity?.room_access_type !== 'public' &&
                            <div className={styles.programField} role="group" aria-label="Prenotazione richiesta"><span>Prenotazione richiesta</span><Select disabled={savingSlot} portal appearance="modern"
                                options={reservableOptions}
                                selected={reservableOptions.find((item) => item.value === (slotForm?.is_reservable !== false)) || reservableOptions[0]}
                                onSelect={(item) => setSlotForm({ ...slotForm, is_reservable: item.value })}
                            /></div>
                        }
                    </div>
                    <div className={styles.programFormRow}>
                        <div style={{ width: '100%' }}>
                            <div className={typo.caption}>Descrizione</div>
                            <MDXEditor
                                className={styles.panelEditor}
                                markdown={slotForm?.description ?? ''}
                                onChange={(value) => {
                                    setSlotForm((prev) => ({
                                        ...prev,
                                        description: value
                                    }))
                                }}
                                plugins={[
                                    headingsPlugin(),
                                    listsPlugin(),
                                    quotePlugin(),
                                    thematicBreakPlugin(),
                                    linkDialogPlugin(),
                                    diffSourcePlugin(),
                                    tablePlugin(),
                                    toolbarPlugin({
                                        toolbarContents: () => (
                                            <>
                                                {' '}
                                                <UndoRedo />
                                                <Separator />
                                                <BoldItalicUnderlineToggles />
                                                <Separator />
                                                <ListsToggle />
                                                <Separator />
                                                <BlockTypeSelect />
                                                <DiffSourceToggleWrapper options={["rich-text", "source"]} />
                                            </>
                                        )
                                    })
                                ]}
                            />
                        </div>
                    </div>
                    <div className={styles.programFormRow}>
                        <div className={styles.programField} role="group" aria-label="Liste ActiveCampaign"><span>Liste ActiveCampaign</span><MultipleSelect disabled={savingSlot} portal appearance="modern"
                            placeholder="Liste Active Campaign Slot"
                            selected={slotLists?.filter(list => slotSelectedLists.map(sl => parseInt(sl.id)).includes(parseInt(list.id)))}
                            onSelect={(items) => {
                                setSlotSelectedLists(items.map(i => ({ id: parseInt(i.id), name: i.name })));
                                setSlotForm((prev) => ({
                                    ...(prev || {}),
                                    ac_lists: items.map(i => parseInt(i.id)).filter((id) => !Number.isNaN(id)),
                                }));
                            }}
                            searchPlaceholder="Cerca per nome"
                            onSearch={searchSlotLists}
                            options={slotLists}
                        /></div>
                    </div>
                    <div className={styles.programFormRow}>
                        <div className={styles.programField} role="group" aria-label="Ospiti"><span>Ospiti</span><MultipleSelect disabled={savingSlot} portal appearance="modern"
                            selectedFirst
                            placeholder="Ospiti del panel"
                            selected={selectedPanelGuests}
                            searchPlaceholder="Cerca ospite"
                            onSearch={() => { }}
                            onSelect={(items) => {
                                const selectedIds = items
                                    .map((item) => parseInt(item.id, 10))
                                    .filter((id) => !Number.isNaN(id));

                                setSlotForm((prev) => ({
                                    ...(prev || {}),
                                    guest_ids: selectedIds,
                                    moderator_ids: (Array.isArray(prev?.moderator_ids) ? prev.moderator_ids : []).filter((id) => !selectedIds.includes(id)),
                                }));
                            }}
                            options={panelGuestOptions}
                        /></div>
                    </div>
                    <div className={styles.programFormRow}>
                        <div className={styles.programField} role="group" aria-label="Moderatori"><span>Moderatori</span><MultipleSelect disabled={savingSlot} portal appearance="modern"
                            selectedFirst
                            placeholder="Moderatori del panel"
                            selected={selectedPanelModerators}
                            searchPlaceholder="Cerca moderatore"
                            onSearch={() => { }}
                            onSelect={(items) => {
                                const selectedIds = items
                                    .map((item) => parseInt(item.id, 10))
                                    .filter((id) => !Number.isNaN(id));

                                setSlotForm((prev) => ({
                                    ...(prev || {}),
                                    moderator_ids: selectedIds,
                                    guest_ids: (Array.isArray(prev?.guest_ids) ? prev.guest_ids : []).filter((id) => !selectedIds.includes(id)),
                                }));
                            }}
                            options={panelGuestOptions}
                        /></div>
                    </div>
                    <h3 className={styles.formSectionTitle}>Orari e capienza</h3>
                    <div className={styles.programFormRow}>
                        <TextInput appearance="modern"
                            type='date'
                            label="Data e ora di inizio" placeholder="Inizio"
                            value={parseEventDate(slotForm?.starts_at)}
                            onKeyUp={(value) => setSlotForm({ ...slotForm, starts_at: value && !Number.isNaN(value.getTime()) ? value.toISOString().split('.')[0] : null })}
                            style={{ width: '100%' }}
                        />
                        <TextInput appearance="modern"
                            type='date'
                            label="Data e ora di fine" placeholder="Fine"
                            value={parseEventDate(slotForm?.ends_at)}
                            onKeyUp={(value) => setSlotForm({ ...slotForm, ends_at: value && !Number.isNaN(value.getTime()) ? value.toISOString().split('.')[0] : null })}
                            style={{ width: '100%' }}
                        />
                    </div>
                    {parseEventDate(slotForm?.starts_at) && parseEventDate(slotForm?.ends_at) && parseEventDate(slotForm.ends_at) < parseEventDate(slotForm.starts_at) && <p className={styles.formError} role="alert">La fine del panel non può precedere l’inizio.</p>}
                    {activity?.room_access_type !== 'public' && slotForm?.is_reservable !== false &&
                        <div className={styles.programFormRow}>
                            <TextInput appearance="modern"
                                type='number'
                                label="Capienza panel prenotabile" placeholder="Numero di posti"
                                value={slotForm?.capacity}
                                onKeyUp={(value) => setSlotForm({ ...slotForm, capacity: value })}
                                style={{ width: '100%' }}
                            />
                        </div>
                    }
                </div>
            </Dialog>
            <Dialog
                overflow="scroll"
                open={!!guest}
                title={guest?.id ? 'Modifica ospite' : 'Aggiungi ospite'}
                onClose={() => { if (!savingGuest && !deletingGuestId) setGuest(null); }}
                action={
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', width: '100%' }}>
                        {guest?.id && (
                            <Button appearance="text" accentColor="var(--sf-red)"
                                disabled={savingGuest || !!deletingGuestId || savingGuestsOrder}
                                onClick={() => deleteGuest(guest)}>
                                {deletingGuestId === guest.id ? "Eliminazione..." : "Elimina"}
                            </Button>
                        )}
                        <Button disabled={saveGuestDisabled || savingGuest || !!deletingGuestId} onClick={saveGuest}>
                            {savingGuest ? "Salvataggio..." : guest?.id ? "Salva" : "Aggiungi"}
                        </Button>
                    </div>
                }
            >
                <div className={styles.ticketTypeDialog}>
                    <div className={styles.ticketTypeRow}>
                        <TextInput appearance="modern"
                            type='text'
                            placeholder={'Nome'}
                            value={guest?.name}
                            onKeyUp={(value) => setGuest((prev) => ({ ...(prev || {}), name: value }))}
                            style={{ width: '100%' }}
                        />
                        <TextInput appearance="modern"
                            type='text'
                            placeholder={'Cognome'}
                            value={guest?.surname}
                            onKeyUp={(value) => setGuest((prev) => ({ ...(prev || {}), surname: value }))}
                            style={{ width: '100%' }}
                        />
                    </div>
                    <div className={styles.ticketTypeRow}>
                        <TextInput appearance="modern"
                            type='text'
                            placeholder={'Ruolo (es. CEO, Giornalista, Trader...)'}
                            value={guest?.role}
                            onKeyUp={(value) => setGuest((prev) => ({ ...(prev || {}), role: value }))}
                            style={{ width: '100%' }}
                        />
                        <TextInput appearance="modern"
                            type='text'
                            placeholder={'Azienda'}
                            value={guest?.company}
                            onKeyUp={(value) => setGuest((prev) => ({ ...(prev || {}), company: value }))}
                            style={{ width: '100%' }}
                        />
                    </div>
                    <div className={styles.ticketTypeRow}>
                        <MultipleSelect portal appearance="modern"
                            placeholder="Corsi associati"
                            selected={selectedGuestCourses}
                            searchPlaceholder="Cerca corso"
                            onSearch={() => { }}
                            onSelect={(items) => {
                                const selectedIds = items
                                    .map((item) => parseInt(item.id, 10))
                                    .filter((id) => !Number.isNaN(id));

                                setGuest((prev) => ({
                                    ...(prev || {}),
                                    course_ids: selectedIds,
                                }));
                            }}
                            options={guestCourseOptions}
                        />
                    </div>
                    <div className={styles.ticketTypeRow}>
                        <TextInput appearance="modern"
                            type='text'
                            placeholder={'LinkedIn URL'}
                            value={guest?.linkedin_url}
                            onKeyUp={(value) => setGuest((prev) => ({ ...(prev || {}), linkedin_url: value }))}
                            style={{ width: '100%' }}
                        />
                        <TextInput appearance="modern"
                            type='text'
                            placeholder={'Website URL'}
                            value={guest?.website_url}
                            onKeyUp={(value) => setGuest((prev) => ({ ...(prev || {}), website_url: value }))}
                            style={{ width: '100%' }}
                        />
                    </div>
                    <div className={styles.ticketTypeRow}>
                        <TextInput appearance="modern"
                            type='text'
                            placeholder={'URL immagine ospite'}
                            value={guest?.image || ''}
                            onKeyUp={(value) => setGuest((prev) => ({ ...(prev || {}), image: value || null }))}
                            style={{ width: '100%' }}
                        />
                    </div>
                    <div className={styles.ticketTypeRow}>
                        <div style={{ width: '100%' }}>
                            <div className={typo.caption}>BIO OSPITE (MARKDOWN)</div>
                            <MDXEditor
                                className={styles.editor}
                                markdown={guest?.bio ?? ''}
                                onChange={(value) => {
                                    setGuest((prev) => ({
                                        ...prev,
                                        bio: value
                                    }))
                                }}
                                plugins={[
                                    headingsPlugin(),
                                    listsPlugin(),
                                    quotePlugin(),
                                    thematicBreakPlugin(),
                                    linkDialogPlugin(),
                                    diffSourcePlugin(),
                                    tablePlugin(),
                                    toolbarPlugin({
                                        toolbarContents: () => (
                                            <>
                                                {' '}
                                                <UndoRedo />
                                                <Separator />
                                                <BoldItalicUnderlineToggles />
                                                <Separator />
                                                <ListsToggle />
                                                <Separator />
                                                <BlockTypeSelect />
                                                <DiffSourceToggleWrapper options={["rich-text", "source"]} />
                                            </>
                                        )
                                    })
                                ]}
                            />
                        </div>
                    </div>
                    <div className={styles.ticketTypeRow}>
                        <div style={{ width: '100%' }}>
                            <div className={typo.caption}>NOTE INTERNE (MARKDOWN)</div>
                            <MDXEditor
                                className={styles.editor}
                                markdown={guest?.notes ?? ''}
                                onChange={(value) => {
                                    setGuest((prev) => ({
                                        ...prev,
                                        notes: value
                                    }))
                                }}
                                plugins={[
                                    headingsPlugin(),
                                    listsPlugin(),
                                    quotePlugin(),
                                    thematicBreakPlugin(),
                                    linkDialogPlugin(),
                                    diffSourcePlugin(),
                                    tablePlugin(),
                                    toolbarPlugin({
                                        toolbarContents: () => (
                                            <>
                                                {' '}
                                                <UndoRedo />
                                                <Separator />
                                                <BoldItalicUnderlineToggles />
                                                <Separator />
                                                <ListsToggle />
                                                <Separator />
                                                <BlockTypeSelect />
                                                <DiffSourceToggleWrapper options={["rich-text", "source"]} />
                                            </>
                                        )
                                    })
                                ]}
                            />
                        </div>
                    </div>
                    <div className={styles.ticketTypeRow}>
                        <Uploader
                            key={`event_guest_image_${guest?.id || 'new'}`}
                            preview={guest?.image}
                            fileName={`event_guest_image_${guest?.id || 'new'}`}
                            type={FileType.Image}
                            onComplete={(uploads) => {
                                setGuest((prev) => ({
                                    ...(prev || {}),
                                    image: uploads?.[0]?.url || null
                                }));
                            }}
                            onDelete={() => {
                                setGuest((prev) => ({
                                    ...(prev || {}),
                                    image: null
                                }));
                            }}
                        />
                    </div>
                </div>
            </Dialog>
            <Dialog appearance="modern" style={{ width: 'min(1000px, 100%)' }}
                open={!!selectedSlotDetails}
                title="Dettaglio panel"
                action={<div className={styles.dialogFooter}><Button appearance="subtle" additionalClass={styles.dangerAction} requiresEdit onClick={() => requestPanelDelete(selectedSlotDetails)}>Elimina panel</Button><Button appearance="subtle" onClick={() => editPanel(selectedSlotDetails)}>Modifica panel</Button></div>}
                onClose={() => {
                    setSelectedSlotDetails(null);
                    setSlotBookings([]);
                }}
            >
                <div className={styles.programDialog}>
                    {selectedSlotDetails && (
                        <>
                            <p className={styles.dialogIntro}>{activity?.name} · {panelTypeOptions.find(item => item.value === selectedSlotDetails.panel_type)?.name || 'Panel'}</p><h2 className={styles.slotDetailsName}>{selectedSlotDetails.name || '—'}</h2>
                            <div className={styles.slotDetailsDescription}>
                                <ReactMarkdown rehypePlugins={[rehypeRaw]}>
                                    {selectedSlotDetails.description || ''}
                                </ReactMarkdown>
                            </div>
                            <div className={styles.slotDetailsMetaGrid}>
                                <div className={styles.slotDetailsMetaItem}>
                                    <div className={styles.slotDetailsMetaLabel}>Data</div>
                                    <div className={styles.slotDetailsMetaValue}>
                                        {selectedSlotDetails.starts_at
                                            ? formatDateV2(selectedSlotDetails.starts_at, { day: '2-digit', month: '2-digit', year: 'numeric' })
                                            : '—'}
                                    </div>
                                </div>
                                <div className={styles.slotDetailsMetaItem}>
                                    <div className={styles.slotDetailsMetaLabel}>Orario inizio</div>
                                    <div className={styles.slotDetailsMetaValue}>
                                        {selectedSlotDetails.starts_at ? formatTimeV2(selectedSlotDetails.starts_at) : '—'}
                                    </div>
                                </div>
                                <div className={styles.slotDetailsMetaItem}>
                                    <div className={styles.slotDetailsMetaLabel}>Orario fine</div>
                                    <div className={styles.slotDetailsMetaValue}>
                                        {selectedSlotDetails.ends_at ? formatTimeV2(selectedSlotDetails.ends_at) : '—'}
                                    </div>
                                </div>
                                <div className={styles.slotDetailsMetaItem}>
                                    <div className={styles.slotDetailsMetaLabel}>Capienza</div>
                                    <div className={styles.slotDetailsMetaValue}>{selectedSlotDetails.is_reservable === false ? 'Accesso libero' : selectedSlotDetails.capacity ?? '—'}</div>
                                </div>
                                <div className={styles.slotDetailsMetaItem}>
                                    <div className={styles.slotDetailsMetaLabel}>Iscritti</div>
                                    <div className={styles.slotDetailsMetaValue}>
                                        {selectedSlotDetails.booked_count ?? slotBookings.length ?? 0}
                                    </div>
                                </div>
                            </div>

                        </>
                    )}

                    <h3 className={styles.formSectionTitle}>Iscritti al panel</h3>
                    {loadingSlotBookings && <Loader label='Caricamento iscritti...' />}
                    {!loadingSlotBookings && slotBookings.length === 0 && (
                        <div className={styles.slotBookingEmpty}>Nessun iscritto a questo panel.</div>
                    )}
                    {!loadingSlotBookings && slotBookings.length > 0 && (
                        <div className={styles.slotDetailsTableWrapper}>
                            <Table appearance="minimal" style={{ maxHeight: '360px', overflowY: 'auto' }}
                                columns={[
                                    { key: 'name', label: 'NOME', type: 'string' },
                                    { key: 'surname', label: 'COGNOME', type: 'string' },
                                    { key: 'email', label: 'EMAIL', type: 'string', wrap: true },
                                    { key: 'current_profession', label: 'PROFESSIONE', type: 'string', wrap: true },
                                    { key: 'sector', label: 'SETTORE', type: 'string', wrap: true },
                                    { key: 'experience_years', label: 'ESPERIENZA', type: 'string' },
                                    { key: 'motivation', label: 'MOTIVAZIONE', type: 'string', wrap: true },
                                    { key: 'created_at', label: 'ISCRITTO IL', type: 'datetime' },
                                ]}
                                elements={slotBookings.map((booking) => ({
                                    id: booking.id,
                                    name: booking.name || '—',
                                    surname: booking.surname || '—',
                                    email: booking.email || '—',
                                    current_profession: booking.current_profession || '—',
                                    sector: booking.sector || '—',
                                    experience_years: booking.experience_years || '—',
                                    motivation: booking.motivation || '—',
                                    created_at: booking.created_at || null,
                                }))}
                                defaultSort={{ key: 'created_at', direction: 'desc' }}
                            />
                        </div>
                    )}
                </div>
            </Dialog>
            <Dialog
            style={{width: '80vw'}}
                overflow="scroll"
                open={!!ticketType}
                title={ticketType?.name ? ticketType.name : 'Aggiungi tipo di biglietto'}
                onClose={() => setTicketType(null)}
                action={
                    <Button disabled={saveDisabled} onClick={saveTicketType}>{ticketType?.id ? "Salva" : "Aggiungi"}</Button>
                }
            >
                <div className={styles.ticketTypeDialog}>
                    <div className={styles.ticketTypeRow}>
                        <TextInput appearance="modern" type='text' placeholder={"Nome"} value={ticketType?.name} onKeyUp={(value) => setTicketType({ ...ticketType, name: value })} style={{ width: '100%' }} />
                        <TextInput appearance="modern" type='number' numberType="float" placeholder={"Prezzo"} value={ticketType?.price} onKeyUp={(value) => setTicketType({ ...ticketType, price: value })} style={{ width: '100%' }} />
                    </div>
                    <div className={styles.ticketTypeRow}>
                        <TextInput appearance="modern" type='number' placeholder={"Quantità Massima"} value={ticketType?.maxQuantity} onKeyUp={(value) => setTicketType({ ...ticketType, maxQuantity: value })} style={{ width: '100%' }} />
                        <Select portal appearance="modern" options={accessLevels} selected={accessLevels.find(level => level.value === ticketType?.accessLevel) ?? accessLevels[0]} onSelect={(item) => setTicketType({ ...ticketType, accessLevel: item.value })} />
                    </div>
                    <div className={styles.ticketTypeRow}>
                        <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', width: '100%', gap: '1rem' }}>
                            <Toggle checked={ticketType?.purchasable ?? false} onChange={(checked) => setTicketType({ ...ticketType, purchasable: checked })} />
                            Acquistabile
                        </div>
                        <MultipleSelect portal appearance="modern"
                            placeholder="Liste Active Campaign"
                            selected={lists?.filter(list => selectedLists.map(sl => parseInt(sl.id)).includes(parseInt(list.id)))}
                            onSelect={(items) => {
                                setSelectedLists(items.map(i => ({ id: parseInt(i.id), name: i.name })));
                            }}
                            searchPlaceholder="Cerca per nome"
                            onSearch={searchLists}
                            options={lists} />
                    </div>
                    <div className={styles.ticketTypeRow}>
                        <div className={styles.ticketTypeColumn}>
                            <div className={typo.caption}>COLORE SFONDO</div>
                            <div className={styles.colorPicker}>
                                <ChromePicker disableAlpha color={ticketType?.google_pass?.backgroundColor} onChange={(color) => {
                                    setTicketType((prev) => ({
                                        ...prev,
                                        google_pass: {
                                            ...(prev?.google_pass ?? {}),
                                            backgroundColor: color.hex
                                        },
                                        apple_pass: {
                                            ...(prev?.apple_pass ?? {}),
                                            backgroundColor: `rgb(${color.rgb.r}, ${color.rgb.g}, ${color.rgb.b})`
                                        }
                                    }))
                                }}
                                />
                            </div>
                        </div>
                        <div className={styles.ticketTypeColumn}>
                            <div className={typo.caption}>COLORE TESTO (SOLO APPLE)</div>
                            <div className={styles.colorPicker}>
                                <ChromePicker disableAlpha color={ticketType?.google_pass?.foregroundColor} onChange={(color) => {
                                    setTicketType((prev) => ({
                                        ...prev,
                                        google_pass: {
                                            ...(prev?.google_pass ?? {}),
                                            foregroundColor: color.hex
                                        },
                                        apple_pass: {
                                            ...(prev?.apple_pass ?? {}),
                                            foregroundColor: `rgb(${color.rgb.r}, ${color.rgb.g}, ${color.rgb.b})`
                                        }
                                    }))
                                }}
                                />
                            </div>
                        </div>
                    </div>
                    <div className={typo.caption}>DESCRIZIONE</div>
                    <div className={styles.ticketTypeRow}>
                        <MDXEditor
                            className={styles.editor}
                            markdown={ticketType?.description ?? ''}
                            onChange={(value) => {
                                setTicketType((prev) => {
                                    prev.description = value
                                    return { ...prev }
                                })
                            }}
                            plugins={[
                                headingsPlugin(),
                                listsPlugin(),
                                quotePlugin(),
                                thematicBreakPlugin(),
                                linkDialogPlugin(),
                                diffSourcePlugin(),
                                tablePlugin(),
                                toolbarPlugin({
                                    toolbarContents: () => (
                                        <>
                                            {' '}
                                            <UndoRedo />
                                            <Separator />
                                            <BoldItalicUnderlineToggles />
                                            <Separator />
                                            <ListsToggle />
                                            <Separator />
                                            <BlockTypeSelect />
                                            <DiffSourceToggleWrapper options={["rich-text", "source"]} />
                                        </>
                                    )
                                })
                            ]}
                        />
                    </div>
                    <div className={styles.ticketTypeRow}>
                        <div className={styles.ticketTypeColumn}>
                            <div className={typo.caption}>ICONA APPLE PASS (29x29)</div>
                            <TextInput appearance="modern"
                                type='text'
                                placeholder={"Apple Pass Icon"}
                                value={ticketType?.apple_pass?.icon}
                                onKeyUp={(value) => setTicketType((prev) => ({
                                    ...prev,
                                    apple_pass: {
                                        ...(prev?.apple_pass ?? {}),
                                        icon: value
                                    }
                                }))}
                                style={{ width: '100%' }}
                            />
                            <Uploader
                                key={'apple_pass_icon'}
                                preview={ticketType?.apple_pass?.icon}
                                iconButtons={true}
                                multiple={false}
                                type={FileType.Image}
                                onComplete={(uploads) => {
                                    setTicketType({ ...ticketType, apple_pass: { ...ticketType.apple_pass, icon: uploads[0].url } })
                                }}
                                onDelete={() => {
                                    setTicketType({ ...ticketType, apple_pass: { ...ticketType.apple_pass, icon: "" } })
                                }}
                            />

                            <div className={typo.caption}>ICONA APPLE PASS 2x(58x58)</div>
                            <TextInput appearance="modern"
                                type='text'
                                placeholder={"Apple Pass Icon 2x"}
                                value={ticketType?.apple_pass?.['icon@2x']}
                                onKeyUp={(value) => setTicketType((prev) => ({
                                    ...prev,
                                    apple_pass: {
                                        ...(prev?.apple_pass ?? {}),
                                        'icon@2x': value
                                    }
                                }))}
                                style={{ width: '100%' }}
                            />
                            <Uploader
                                key={'apple_pass_icon_2x'}
                                preview={ticketType?.apple_pass?.['icon@2x']}
                                iconButtons={true}
                                multiple={false}
                                type={FileType.Image}
                                onComplete={(uploads) => {
                                    setTicketType({ ...ticketType, apple_pass: { ...ticketType.apple_pass, 'icon@2x': uploads[0].url } })
                                }}
                                onDelete={() => {
                                    setTicketType({ ...ticketType, apple_pass: { ...ticketType.apple_pass, 'icon@2x': "" } })
                                }}
                            />

                            <div className={typo.caption}>ICONA APPLE PASS 3x(87x87)</div>
                            <TextInput appearance="modern"
                                type='text'
                                placeholder={"Apple Pass Icon 3x"}
                                value={ticketType?.apple_pass?.['icon@3x']}
                                onKeyUp={(value) => setTicketType((prev) => ({
                                    ...prev,
                                    apple_pass: {
                                        ...(prev?.apple_pass ?? {}),
                                        'icon@3x': value
                                    }
                                }))}
                                style={{ width: '100%' }}
                            />
                            <Uploader
                                key={'apple_pass_icon_3x'}
                                preview={ticketType?.apple_pass?.['icon@3x']}
                                iconButtons={true}
                                multiple={false}
                                type={FileType.Image}
                                onComplete={(uploads) => {
                                    setTicketType({ ...ticketType, apple_pass: { ...ticketType.apple_pass, 'icon@3x': uploads[0].url } })
                                }}
                                onDelete={() => {
                                    setTicketType({ ...ticketType, apple_pass: { ...ticketType.apple_pass, 'icon@3x': "" } })
                                }}
                            />
                        </div>
                        <div className={styles.ticketTypeColumn}>
                            <div className={typo.caption}>ICONA GOOGLE PASS</div>
                            <TextInput appearance="modern"
                                type='text'
                                placeholder={"Google Pass Icon"}
                                value={ticketType?.google_pass?.icon}
                                onKeyUp={(value) => setTicketType((prev) => ({
                                    ...prev,
                                    google_pass: {
                                        ...(prev?.google_pass ?? {}),
                                        icon: value
                                    }
                                }))}
                                style={{ width: '100%' }}
                            />
                            <Uploader
                                key={'google_pass_icon'}
                                preview={ticketType?.google_pass?.icon}
                                iconButtons={true}
                                multiple={false}
                                type={FileType.Image}
                                onComplete={(uploads) => {
                                    setTicketType({ ...ticketType, google_pass: { ...ticketType.google_pass, icon: uploads[0].url } })
                                }}
                                onDelete={() => {
                                    setTicketType({ ...ticketType, google_pass: { ...ticketType.google_pass, icon: null } })
                                }}
                            />
                        </div>
                    </div>
                    <div className={styles.ticketTypeRow}>
                        <div className={styles.ticketTypeColumn}>
                            <div className={typo.caption}>LOGO APPLE PASS (160x50)</div>
                            <TextInput appearance="modern"
                                type='text'
                                placeholder={"Apple Pass Logo"}
                                value={ticketType?.apple_pass?.logo}
                                onKeyUp={(value) => setTicketType((prev) => ({
                                    ...prev,
                                    apple_pass: {
                                        ...(prev?.apple_pass ?? {}),
                                        logo: value
                                    }
                                }))}
                                style={{ width: '100%' }}
                            />
                            <Uploader
                                key={'apple_pass_logo'}
                                preview={ticketType?.apple_pass?.logo}
                                iconButtons={true}
                                multiple={false}
                                type={FileType.Image}
                                onComplete={(uploads) => {
                                    setTicketType({ ...ticketType, apple_pass: { ...ticketType.apple_pass, logo: uploads[0].url } })
                                }}
                                onDelete={() => {
                                    setTicketType({ ...ticketType, apple_pass: { ...ticketType.apple_pass, logo: null } })
                                }}
                            />
                            <div className={typo.caption}>LOGO APPLE PASS 2x (320x100)</div>
                            <TextInput appearance="modern"
                                type='text'
                                placeholder={"Apple Pass Logo 2x"}
                                value={ticketType?.apple_pass?.["logo@2x"]}
                                onKeyUp={(value) => setTicketType((prev) => ({
                                    ...prev,
                                    apple_pass: {
                                        ...(prev?.apple_pass ?? {}),
                                        "logo@2x": value
                                    }
                                }))}
                                style={{ width: '100%' }}
                            />
                            <Uploader
                                key={'apple_pass_logo_2x'}
                                preview={ticketType?.apple_pass?.["logo@2x"]}
                                iconButtons={true}
                                multiple={false}
                                type={FileType.Image}
                                onComplete={(uploads) => {
                                    setTicketType({ ...ticketType, apple_pass: { ...ticketType.apple_pass, "logo@2x": uploads[0].url } })
                                }}
                                onDelete={() => {
                                    setTicketType({ ...ticketType, apple_pass: { ...ticketType.apple_pass, "logo@2x": null } })
                                }}
                            />
                            <div className={typo.caption}>LOGO APPLE PASS 3x (480x150)</div>
                            <TextInput appearance="modern"
                                type='text'
                                placeholder={"Apple Pass Logo 3x"}
                                value={ticketType?.apple_pass?.["logo@3x"]}
                                onKeyUp={(value) => setTicketType((prev) => ({
                                    ...prev,
                                    apple_pass: {
                                        ...(prev?.apple_pass ?? {}),
                                        "logo@3x": value
                                    }
                                }))}
                                style={{ width: '100%' }}
                            />
                            <Uploader
                                key={'apple_pass_logo_3x'}
                                preview={ticketType?.apple_pass?.["logo@3x"]}
                                iconButtons={true}
                                multiple={false}
                                type={FileType.Image}
                                onComplete={(uploads) => {
                                    setTicketType({ ...ticketType, apple_pass: { ...ticketType.apple_pass, "logo@3x": uploads[0].url } })
                                }}
                                onDelete={() => {
                                    setTicketType({ ...ticketType, apple_pass: { ...ticketType.apple_pass, "logo@3x": null } })
                                }}
                            />
                        </div>
                        <div className={styles.ticketTypeColumn}>
                            <div className={typo.caption}>LOGO GOOGLE PASS</div>
                            <TextInput appearance="modern"
                                type='text'
                                placeholder={"Google Pass Logo"}
                                value={ticketType?.google_pass?.logo}
                                onKeyUp={(value) => setTicketType((prev) => ({
                                    ...prev,
                                    google_pass: {
                                        ...(prev?.google_pass ?? {}),
                                        logo: value
                                    }
                                }))}
                                style={{ width: '100%' }}
                            />
                            <Uploader
                                key={'google_pass_logo'}
                                preview={ticketType?.google_pass?.logo}
                                iconButtons={true}
                                multiple={false}
                                type={FileType.Image}
                                onComplete={(uploads) => {
                                    setTicketType({ ...ticketType, google_pass: { ...ticketType.google_pass, logo: uploads[0].url } })
                                }}
                                onDelete={() => {
                                    setTicketType({ ...ticketType, google_pass: { ...ticketType.google_pass, logo: null } })
                                }}
                            />
                        </div>
                    </div>
                    <div className={styles.ticketTypeRow}>
                        <div className={styles.ticketTypeColumn}>
                            <div className={typo.caption}>HERO APPLE PASS</div>
                            <TextInput appearance="modern"
                                type='text'
                                placeholder={"Apple Pass Hero"}
                                value={ticketType?.apple_pass?.strip}
                                onKeyUp={(value) => setTicketType((prev) => ({
                                    ...prev,
                                    apple_pass: {
                                        ...(prev?.apple_pass ?? {}),
                                        strip: value
                                    }
                                }))}
                                style={{ width: '100%' }}
                            />
                            <Uploader
                                key={'apple_pass_strip'}
                                preview={ticketType?.apple_pass?.strip}
                                iconButtons={true}
                                multiple={false}
                                type={FileType.Image}
                                onComplete={(uploads) => {
                                    setTicketType({ ...ticketType, apple_pass: { ...ticketType.apple_pass, strip: uploads[0].url } })
                                }}
                                onDelete={() => {
                                    setTicketType({ ...ticketType, apple_pass: { ...ticketType.apple_pass, strip: null } })
                                }}
                            />
                        </div>
                        <div className={styles.ticketTypeColumn}>
                            <div className={typo.caption}>HERO GOOGLE PASS</div>
                            <TextInput appearance="modern"
                                type='text'
                                placeholder={"Google Pass Hero"}
                                value={ticketType?.google_pass?.strip}
                                onKeyUp={(value) => setTicketType((prev) => ({
                                    ...prev,
                                    google_pass: {
                                        ...(prev?.google_pass ?? {}),
                                        strip: value
                                    }
                                }))}
                                style={{ width: '100%' }}
                            />
                            <Uploader
                                key={'google_pass_strip'}
                                preview={ticketType?.google_pass?.strip}
                                iconButtons={true}
                                multiple={false}
                                type={FileType.Image}
                                onComplete={(uploads) => {
                                    setTicketType({ ...ticketType, google_pass: { ...ticketType.google_pass, strip: uploads[0].url } })
                                }}
                                onDelete={() => {
                                    setTicketType({ ...ticketType, google_pass: { ...ticketType.google_pass, strip: null } })
                                }}
                            />
                        </div>
                    </div>
                    {ticketType && event &&
                        <div className={styles.ticketTypeRow}>
                            <div className={styles.ticketTypeColumn} style={{ alignItems: 'center' }}>
                                <div className={typo.caption}>PREVIEW APPLE</div>

                                <div className={styles.applePassPreview} style={{ backgroundColor: ticketType?.apple_pass?.backgroundColor, color: ticketType?.apple_pass?.foregroundColor }}>
                                    <div className={styles.circle}></div>
                                    <div className={styles.previewRow}>
                                        <img src={ticketType?.apple_pass?.['logo@3x']} alt="logo" className={styles.applePassLogo} />
                                        <div className={styles.applePassHeader}>
                                            <div className={styles.applePassTimeShort}>{formatTimeV2(event?.starts_at)}</div>
                                            <div className={styles.applePassDateShort}>{formatDateV2(event?.starts_at, { day: 'numeric', month: 'short' })}</div>
                                        </div>
                                    </div>
                                    <div className={styles.previewRow}>
                                        <img src={ticketType?.apple_pass?.strip} alt="icon" className={styles.applePassStrip} />
                                    </div>
                                    <div className={styles.previewRow}>
                                        <div className={styles.previewColumn}>
                                            <div className={styles.previewLabel}>DATA</div>
                                            <div className={styles.previewValue}>{formatDateV2(event?.starts_at, { day: 'numeric', month: 'long', year: 'numeric' })}</div>
                                        </div>
                                        <div className={styles.previewColumn}>
                                            <div className={styles.previewLabel}>ORARIO</div>
                                            <div className={styles.previewValue}>{formatTimeV2(event?.starts_at)} - {formatTimeV2(event?.ends_at)}</div>
                                        </div>
                                    </div>
                                    <div className={styles.previewRow}>
                                        <div className={styles.previewColumn}>
                                            <div className={styles.previewLabel}>LOCATION</div>
                                            <div className={styles.previewValue}>{event.location?.name ?? ''}</div>
                                        </div>
                                    </div>
                                    <div className={styles.previewRow} style={{ height: '100%', display: 'flex', flex: '1', flexDirection: 'column', justifyContent: 'flex-end', alignItems: 'center', width: '100%' }}>
                                        <div className={styles.previewLabel} style={{ minHeight: 'unset' }}>
                                            <img src={qrCodePlaceholder} alt="qrcode" className={styles.qrCodePlaceholder} />
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className={styles.ticketTypeColumn} style={{ alignItems: 'center' }}>
                                <div className={typo.caption}>PREVIEW GOOGLE</div>

                                <div className={styles.googlePassPreview} style={{ backgroundColor: ticketType?.google_pass?.backgroundColor, color: getTextColor(ticketType?.google_pass?.backgroundColor) }}>
                                    <div className={styles.previewRow}>
                                        <img src={ticketType?.google_pass?.logo} alt="logo" className={styles.googlePassLogo} />
                                    </div>
                                    <div className={styles.previewRow}>
                                        <div className={styles.previewColumn}>
                                            <div className={styles.previewValue}>{event.location?.name ?? ''}</div>
                                        </div>
                                    </div>
                                    <div className={styles.previewRow}>
                                        <div className={styles.previewEventName}>{event.name}</div>
                                    </div>

                                    <div className={styles.previewRow}>
                                        <div className={styles.previewColumn}>
                                            <div className={styles.previewLabel}>Data</div>
                                            <div className={styles.previewValue}>{formatDateV2(event?.starts_at, { day: 'numeric', month: 'short', year: 'numeric' })}</div>
                                        </div>
                                        <div className={styles.previewColumn}>
                                            <div className={styles.previewLabel} style={{ textAlign: 'right' }}>Ora</div>
                                            <div className={styles.previewValue}>{formatTimeV2(event?.starts_at)}</div>
                                        </div>
                                    </div>

                                    <div className={styles.previewRow} style={{ height: '100%', display: 'flex', flex: '1', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
                                        <div className={styles.previewLabel} style={{ minHeight: 'unset' }}>
                                            <img src={qrCodePlaceholder} alt="qrcode" className={styles.googleQrCodePlaceholder} />
                                        </div>
                                    </div>
                                    <div className={styles.previewRow}>
                                        <img src={ticketType?.google_pass?.strip} alt="icon" className={styles.googlePassStrip} />
                                    </div>
                                </div>
                            </div>
                        </div>
                    }
                </div>
            </Dialog>
            <Dialog
                overflow="scroll"
                open={!!stand}
                title={stand?.id ? 'Modifica stand' : 'Aggiungi stand'}
                onClose={() => setStand(null)}
                action={
                    <Button disabled={saveStandDisabled || savingStand} onClick={saveStand}>
                        {savingStand ? "Salvataggio..." : stand?.id ? "Salva" : "Aggiungi"}
                    </Button>
                }
            >
                <div className={styles.ticketTypeDialog}>
                    <div className={styles.ticketTypeRow}>
                        <TextInput appearance="modern"
                            type='text'
                            placeholder={'Nome stand'}
                            value={stand?.name}
                            onKeyUp={(value) => setStand((prev) => ({ ...(prev || {}), name: value }))}
                            style={{ width: '100%' }}
                        />
                    </div>
                    <div className={styles.ticketTypeRow}>
                        <TextInput appearance="modern" type="number" label="Numero stand sulla mappa" placeholder="Numero stand (opzionale)" min={1} max={2147483647}
                            value={stand?.stand_number ?? ''}
                            onKeyUp={(value) => setStand(prev => ({ ...prev, stand_number: value }))}
                            style={{ width: '100%' }} />
                    </div>
                    <p>Inserisci il numero della posizione sulla mappa. Più aziende possono condividere lo stesso numero. Lascia vuoto per non associare una posizione.</p>
                    <div className={styles.ticketTypeRow}>
                        <Select portal appearance="modern"
                            selected={selectedStandCompany}
                            options={filteredEventPartnerCompanies}
                            nullable
                            nullLabel='Nessuna azienda associata'
                            placeholder='Associa azienda Event Partner'
                            searchPlaceholder='Cerca azienda'
                            onSearch={(value) => {
                                const query = String(value || '').trim().toLowerCase();
                                setFilteredEventPartnerCompanies(query
                                    ? eventPartnerCompanies.filter((company) => String(company.name || '').toLowerCase().includes(query))
                                    : eventPartnerCompanies);
                            }}
                            onSelect={(company) => {
                                setStand((prev) => ({
                                    ...(prev || {}),
                                    company_id: company?.value || null,
                                    company_name: company?.name || prev?.company_name || '',
                                    company_logo: company?.logo || prev?.company_logo || '',
                                }));
                            }}
                        />
                    </div>
                    <div className={styles.ticketTypeRow}>
                        <TextInput appearance="modern"
                            type='text'
                            placeholder={'Nome azienda'}
                            value={stand?.company_name}
                            onKeyUp={(value) => setStand((prev) => ({ ...(prev || {}), company_name: value }))}
                            style={{ width: '100%' }}
                        />
                    </div>
                    <div className={styles.ticketTypeRow}>
                        <TextInput appearance="modern"
                            type='text'
                            placeholder={'Logo azienda (URL)'}
                            value={stand?.company_logo}
                            onKeyUp={(value) => setStand((prev) => ({ ...(prev || {}), company_logo: value }))}
                            style={{ width: '100%' }}
                        />
                    </div>
                    <div className={styles.ticketTypeRow}>
                        <Uploader
                            preview={stand?.company_logo || ''}
                            multiple={false}
                            type={FileType.Image}
                            iconButtons={true}
                            onComplete={(uploads) => {
                                setStand((prev) => ({
                                    ...(prev || {}),
                                    company_logo: uploads?.[0]?.url || ''
                                }))
                            }}
                            onDelete={() => {
                                setStand((prev) => ({
                                    ...(prev || {}),
                                    company_logo: ''
                                }))
                            }}
                        />
                    </div>
                    <div className={styles.ticketTypeRow}>
                        <textarea
                            className={styles.standDescriptionArea}
                            placeholder='Descrizione stand'
                            value={stand?.description || ''}
                            onChange={(e) => setStand((prev) => ({ ...(prev || {}), description: e.target.value }))}
                        />
                    </div>
                </div>
            </Dialog>
            <Dialog
                open={!!standTalentQr}
                title='QR Talent Village'
                onClose={() => !loadingStandTalentQr && setStandTalentQr(null)}
            >
                <div className={styles.standTalentQrDialog}>
                    <div className={styles.standTalentQrCopy}>
                        <strong>{standTalentQr?.stand?.name || 'Stand'}</strong>
                        <span>Scansionando questo QR l'utente potrà condividere il CV esclusivamente con l'azienda associata allo stand.</span>
                    </div>
                    {loadingStandTalentQr ? (
                        <Skeleton width='240px' height='240px' />
                    ) : standTalentQr?.data?.qr_code ? (
                        <img
                            className={styles.standTalentQrImage}
                            src={`data:image/png;base64,${standTalentQr.data.qr_code}`}
                            alt={`QR Talent Village ${standTalentQr?.stand?.name || ''}`}
                        />
                    ) : null}
                    {standTalentQr?.data?.url ? (
                        <a className={styles.standTalentQrUrl} href={standTalentQr.data.url} target='_blank' rel='noopener noreferrer'>
                            {standTalentQr.data.url}
                        </a>
                    ) : null}
                    <Button disabled={loadingStandTalentQr || !standTalentQr?.data?.qr_code} onClick={downloadStandTalentQr}>
                        Scarica QR code
                    </Button>
                </div>
            </Dialog>

            <Dialog appearance="modern" open={!!panelDelete} title="Elimina panel"
                style={{ width: 'min(520px, 100%)' }}
                onClose={() => { if (!deletingPanel) setPanelDelete(null); }}
                action={<div className={styles.dialogFooter}>
                    <Button appearance="subtle" disabled={deletingPanel} onClick={() => setPanelDelete(null)}>Annulla</Button>
                    <Button accentColor="var(--sf-red)" requiresEdit disabled={deletingPanel} onClick={deletePanel}>{deletingPanel ? 'Eliminazione…' : 'Elimina panel'}</Button>
                </div>}>
                <div className={styles.programDialog}>
                    <p>Eliminare <strong>{panelDelete?.slot.name}</strong> dal programma?</p>
                    <p className={styles.dialogIntro}>Il panel verrà rimosso anche dalle agende personali e le relative prenotazioni saranno annullate. L’operazione non può essere annullata dal backoffice.</p>
                    {panelDeleteError && <p className={styles.formError} role="alert">{panelDeleteError}</p>}
                </div>
            </Dialog>
        </HeaderFooterLayout >
    )

}

export default Event;
